import React, { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { 
  Heart, MapPin, Activity, DollarSign, Clock, ChevronRight, ChevronLeft, 
  Download, Mail, Shield, Check, AlertTriangle, HelpCircle, Home, Users,
  Building2, Bed, Brain, X, Star, Eye, TrendingUp, Search, MessageCircle,
  ThumbsUp, Send, Lock, Phone, Award, BookOpen, UserCheck, Scale, Menu, LogOut, User,
  BarChart3, Settings, FileText, ChevronDown, ChevronUp, RefreshCw
} from "lucide-react";

// Configure axios defaults
axios.defaults.withCredentials = true;

// Use current origin for API calls - works on both preview and custom domains
const API = `${window.location.origin}/api`;

// Utility to generate session ID
const generateSessionId = () => `cm_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Format care type from snake_case to Title Case
const formatCareType = (type) => {
  if (!type) return '';
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Map care_path.primary values to Provider Directory category names
const carePathToCategory = (primary) => {
  const map = {
    memory_care: "Long-Term Care",
    long_term_care: "Long-Term Care",
    assisted_living: "Retirement Living",
    home_care: "Home Care",
    enhanced_home_care: "Home Care",
  };
  return map[primary] || "";
};

// Parse markdown-style formatting to HTML
const parseMarkdown = (text) => {
  if (!text) return '';
  return text
    // Bold: **text** or __text__
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_]+)__/g, '<strong>$1</strong>')
    // Italic: *text* or _text_
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    // Line breaks
    .replace(/\n/g, '<br/>');
};

// Social Proof Component - Live activity indicator
const SocialProof = () => {
  const [viewingNow, setViewingNow] = useState(0);
  const [recentCompletions, setRecentCompletions] = useState(0);
  
  useEffect(() => {
    // Initialize with realistic numbers
    setViewingNow(Math.floor(Math.random() * 8) + 12); // 12-19 people
    setRecentCompletions(Math.floor(Math.random() * 15) + 35); // 35-49 today
    
    // Simulate real-time activity fluctuations
    const interval = setInterval(() => {
      setViewingNow(prev => {
        const change = Math.random() > 0.5 ? 1 : -1;
        const newVal = prev + change;
        return Math.max(8, Math.min(25, newVal)); // Keep between 8-25
      });
    }, 4000 + Math.random() * 3000); // Random interval 4-7 seconds
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 py-3 px-4" data-testid="social-proof">
      <div className="flex items-center gap-2 text-sm">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: '#34694D' }}></span>
          <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: '#34694D' }}></span>
        </span>
        <Eye className="w-4 h-4" style={{ color: '#1E3F30' }} />
        <span style={{ color: '#5A6B63' }}>
          <strong style={{ color: '#1E3F30' }}>{viewingNow}</strong> people viewing results now
        </span>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <TrendingUp className="w-4 h-4" style={{ color: '#1E3F30' }} />
        <span style={{ color: '#5A6B63' }}>
          <strong style={{ color: '#1E3F30' }}>{recentCompletions}</strong> families helped today
        </span>
      </div>
    </div>
  );
};

// Trust Section with Rotating Diverse Images
const DIVERSE_FAMILY_IMAGES = [
  { url: "https://images.pexels.com/photos/7551583/pexels-photo-7551583.jpeg?auto=compress&cs=tinysrgb&w=600", alt: "Multigenerational family smiling together" },
  { url: "https://images.unsplash.com/photo-1666885181574-b93c72ff7eac?w=600", alt: "Black family members caring for elderly parent" },
  { url: "https://images.pexels.com/photos/18394079/pexels-photo-18394079.jpeg?auto=compress&cs=tinysrgb&w=600", alt: "South Asian grandparents with grandchild" },
  { url: "https://images.unsplash.com/photo-1771924368978-f0a985a2ee0c?w=600", alt: "Asian multi-generational family outdoors" },
  { url: "https://images.pexels.com/photos/7551678/pexels-photo-7551678.jpeg?auto=compress&cs=tinysrgb&w=600", alt: "Elderly person receiving care assistance" },
  { url: "https://images.unsplash.com/photo-1578496781307-30c2b531c05a?w=600", alt: "African American family gathered together" },
];

const TrustSection = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentImageIndex((prev) => (prev + 1) % DIVERSE_FAMILY_IMAGES.length);
        setIsTransitioning(false);
      }, 500);
    }, 5000); // Change every 5 seconds
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="card">
          <div className="flex items-start gap-6 flex-col md:flex-row">
            <div className="relative w-full md:w-56 h-56 rounded-xl overflow-hidden flex-shrink-0">
              <img 
                src={DIVERSE_FAMILY_IMAGES[currentImageIndex].url}
                alt={DIVERSE_FAMILY_IMAGES[currentImageIndex].alt}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
              />
              {/* Image indicators */}
              <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
                {DIVERSE_FAMILY_IMAGES.map((img, index) => (
                  <button
                    key={img.alt || index}
                    onClick={() => { setIsTransitioning(true); setTimeout(() => { setCurrentImageIndex(index); setIsTransitioning(false); }, 300); }}
                    className={`w-2 h-2 rounded-full transition-all ${index === currentImageIndex ? 'w-4' : ''}`}
                    style={{ backgroundColor: index === currentImageIndex ? 'white' : 'rgba(255,255,255,0.5)' }}
                    aria-label={`View image ${index + 1}`}
                  />
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-heading text-2xl mb-3" style={{ color: '#232F2A' }}>
                We Understand This is Hard
              </h3>
              <p className="mb-4" style={{ color: '#5A6B63' }}>
                Making care decisions for aging parents is one of life's most challenging moments. 
                CareMap Canada was built to give you clarity during confusion, and confidence during uncertainty.
              </p>
              <p className="mb-4" style={{ color: '#5A6B63' }}>
                We serve families from all backgrounds across Canada, understanding that every culture has its own approach to elder care.
              </p>
              <p className="text-sm" style={{ color: '#5A6B63' }}>
                <em>This tool provides general guidance only. Always consult healthcare and financial professionals.</em>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Reusable Pricing Modal Component
const PricingModal = ({ show, onClose }) => {
  const { user } = useAuth();
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    if (show && !pricing) {
      axios.get(`${API}/pricing`)
        .then(res => setPricing(res.data.packages))
        .catch(e => console.error("Error fetching pricing:", e));
    }
  }, [show, pricing]); // eslint-disable-line react-hooks/exhaustive-deps -- API and axios are stable module-level constants

  const handleUpgrade = async (packageId) => {
    if (packageId === "free") {
      onClose();
      return;
    }
    try {
      const res = await axios.post(`${API}/checkout/create`, {
        package_id: packageId,
        session_id: `upgrade-${Date.now()}`,
        origin_url: window.location.origin,
        user_email: user?.email || null
      });
      window.location.href = res.data.url;
    } catch (e) {
      console.error("Checkout error:", e);
    }
  };

  if (!show || !pricing) return null;

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="pricing-modal">
      <div className="bg-white rounded-3xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-heading text-3xl" style={{ color: '#232F2A' }}>Choose Your Plan</h2>
          <button onClick={onClose} data-testid="close-pricing-btn">
            <X className="w-6 h-6" style={{ color: '#5A6B63' }} />
          </button>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {Object.entries(pricing).map(([id, pkg]) => (
            <div 
              key={id} 
              className={`card ${id === "pro" ? 'ring-2' : ''}`}
              style={{ borderColor: id === "pro" ? '#1E3F30' : undefined }}
              data-testid={`pricing-${id}`}
            >
              {id === "pro" && (
                <div className="text-xs font-medium px-3 py-1 rounded-full inline-block mb-4" style={{ backgroundColor: '#1E3F30', color: 'white' }}>
                  Most Popular
                </div>
              )}
              <h3 className="font-heading text-2xl mb-2" style={{ color: '#232F2A' }}>{pkg.name}</h3>
              <div className="mb-4">
                <span className="text-4xl font-heading" style={{ color: '#1E3F30' }}>
                  ${pkg.amount}
                </span>
                {pkg.amount > 0 && <span className="text-sm" style={{ color: '#5A6B63' }}> CAD</span>}
              </div>
              <ul className="space-y-2 mb-6">
                {pkg.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm" style={{ color: '#5A6B63' }}>
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#34694D' }} />
                    {f}
                  </li>
                ))}
              </ul>
              <button 
                onClick={() => handleUpgrade(id)}
                className={`w-full py-3 rounded-full font-medium transition-colors ${
                  id === "free" ? 'btn-secondary' : id === "premium" ? 'btn-accent' : 'btn-primary'
                }`}
                data-testid={`select-${id}-btn`}
              >
                {id === "free" ? "Continue Free" : `Get ${pkg.name}`}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// FAQ Accordion Item
const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  const testId = question.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40);
  return (
    <div
      className="rounded-xl overflow-hidden transition-all"
      style={{ backgroundColor: '#FFFFFF', border: '1px solid #E6EDE8' }}
      data-testid={`faq-item-${testId}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left bg-transparent border-0 cursor-pointer"
      >
        <span className="font-medium text-sm sm:text-base pr-4" style={{ color: '#232F2A' }}>{question}</span>
        <ChevronRight
          className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
          style={{ color: '#5A6B63' }}
        />
      </button>
      {open && (
        <div className="px-5 pb-5 -mt-1">
          <p className="text-sm leading-relaxed" style={{ color: '#5A6B63' }}>{answer}</p>
        </div>
      )}
    </div>
  );
};

// Landing Page - Amanda Sabreah Style
const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !showExitIntent) {
        setShowExitIntent(true);
      }
    };
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [showExitIntent]);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      {/* Header */}
      <header className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl" style={{ color: '#1E3F30' }}>CareMap Canada™</h1>
            <p className="text-sm" style={{ color: '#5A6B63' }}>Your guide through elder care decisions.</p>
          </div>
          <div className="flex items-center gap-3 sm:hidden">
            {user ? (
              <Link to="/dashboard" className="text-sm flex items-center gap-1.5" style={{ color: '#1E3F30' }} data-testid="mobile-dashboard-link">
                <User className="w-4 h-4" />
                {user.name?.split(' ')[0]}
              </Link>
            ) : (
              <Link to="/login" className="text-sm font-medium px-3 py-1.5 rounded-full" style={{ backgroundColor: '#E6EDE8', color: '#1E3F30' }} data-testid="mobile-signin-link">
                Sign In
              </Link>
            )}
            <button
              onClick={() => setMobileNavOpen(!mobileNavOpen)}
              className="p-1.5"
              data-testid="landing-mobile-menu-btn"
            >
              <Menu className="w-5 h-5" style={{ color: '#1E3F30' }} />
            </button>
          </div>
          <div className="hidden sm:flex items-center gap-4">
            <Link to="/articles" className="text-sm" style={{ color: '#5A6B63' }} data-testid="landing-articles-link">Articles</Link>
            <Link to="/faq" className="text-sm" style={{ color: '#5A6B63' }}>FAQ</Link>
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" className="text-xs px-2 py-1 rounded-full flex items-center gap-1 hover:opacity-80 transition-opacity" style={{ backgroundColor: '#1E3F30', color: 'white' }} data-testid="landing-admin-link">
                    <Settings className="w-3 h-3" />
                    Admin
                  </Link>
                )}
                <Link to="/dashboard" className="flex items-center gap-2 text-sm hover:opacity-80" style={{ color: '#232F2A' }} data-testid="landing-dashboard-link">
                  <User className="w-4 h-4" style={{ color: '#1E3F30' }} />
                  {user.name}
                  {user.tier !== "free" && (
                    <span className="text-xs px-2 py-0.5 rounded-full uppercase" style={{ backgroundColor: user.tier === "premium" ? '#C26D5C' : '#1E3F30', color: 'white' }}>
                      {user.tier}
                    </span>
                  )}
                </Link>
              </>
            ) : (
              <Link to="/login" className="text-sm" style={{ color: '#5A6B63' }} data-testid="landing-signin-link">Sign In</Link>
            )}
            <button 
              onClick={() => navigate("/assessment")}
              className="btn-primary text-sm py-2 px-5"
              data-testid="header-cta-btn"
            >
              Get Your CareMap
            </button>
          </div>
        </div>
        {/* Mobile Dropdown Menu */}
        {mobileNavOpen && (
          <div className="sm:hidden py-3 mt-2 border-t flex flex-col gap-2" style={{ borderColor: '#D5DFD9' }} data-testid="landing-mobile-menu">
            <Link to="/articles" className="px-2 py-2 text-sm" style={{ color: '#5A6B63' }} onClick={() => setMobileNavOpen(false)}>Articles</Link>
            <Link to="/faq" className="px-2 py-2 text-sm" style={{ color: '#5A6B63' }} onClick={() => setMobileNavOpen(false)}>FAQ</Link>
            {user && user.role === "admin" && (
              <Link to="/admin" className="px-2 py-2 text-sm flex items-center gap-1.5" style={{ color: '#1E3F30' }} onClick={() => setMobileNavOpen(false)}>
                <Settings className="w-3.5 h-3.5" /> Admin Panel
              </Link>
            )}
            <Link
              to="/assessment"
              className="btn-primary text-sm text-center mt-1"
              onClick={() => setMobileNavOpen(false)}
            >
              Get Your CareMap
            </Link>
          </div>
        )}
      </header>

      {/* HERO SECTION */}
      <section className="relative py-20 md:py-32 px-4 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1516733968668-dbdce39c4651?w=1600')` }}
        />
        <div className="relative max-w-4xl mx-auto text-center">
          <p className="text-sm uppercase tracking-widest mb-6 animate-fadeIn" style={{ color: '#5A6B63' }}>
            Built for Canadian families navigating real care decisions
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-tight mb-8 animate-fadeIn" style={{ color: '#232F2A' }}>
            When everything feels uncertain…<br />
            <span style={{ color: '#1E3F30' }}>know what to do next.</span>
          </h2>
          <p className="text-lg sm:text-xl max-w-2xl mx-auto mb-10 animate-fadeIn stagger-1" style={{ color: '#5A6B63' }}>
            CareMap Canada helps you understand your parent's care options, what it will cost, and what to do next — in minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8 animate-fadeIn stagger-2">
            <button 
              onClick={() => navigate("/assessment")}
              className="btn-primary text-lg flex items-center justify-center gap-2"
              data-testid="start-assessment-btn"
            >
              Get Your CareMap
              <ChevronRight className="w-5 h-5" />
            </button>
            <button 
              onClick={() => scrollToSection('how-it-works')}
              className="btn-secondary text-lg"
              data-testid="how-it-works-btn"
            >
              See how it works
            </button>
          </div>
          <p className="text-sm italic animate-fadeIn stagger-3" style={{ color: '#5A6B63' }}>
            You don't have to figure this out alone.
          </p>
          
          {/* Social Proof */}
          <div className="mt-12">
            <SocialProof />
          </div>
        </div>
      </section>

      {/* SECTION 2 — THE PAIN */}
      <section className="py-24 px-4" style={{ backgroundColor: '#E6EDE8' }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-tight mb-8" style={{ color: '#232F2A' }}>
            This isn't just a decision.<br />
            <span style={{ color: '#1E3F30' }}>It's one of the hardest moments in your life.</span>
          </h2>
          <p className="text-lg leading-relaxed mb-6" style={{ color: '#5A6B63' }}>
            You're trying to do the right thing. But everything feels unclear. Costs are confusing. Options don't make sense. And every choice carries weight.
          </p>
          <p className="text-lg" style={{ color: '#5A6B63' }}>
            Most families feel this way. They just don't have a system to guide them.
          </p>
        </div>
      </section>

      {/* SECTION 3 — THE SHIFT */}
      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-tight mb-8" style={{ color: '#232F2A' }}>
              This isn't another website.<br />
              <span style={{ color: '#1E3F30' }}>It's a decision system.</span>
            </h2>
            <p className="text-lg" style={{ color: '#5A6B63' }}>
              CareMap Canada turns your situation into a clear plan.
            </p>
          </div>
          
          <div className="card max-w-2xl mx-auto">
            <p className="font-medium mb-6" style={{ color: '#232F2A' }}>In minutes, you'll understand:</p>
            <ul className="space-y-4">
              {[
                "What type of care your parent actually needs",
                "What it will likely cost in your province",
                "What may be covered — and what won't",
                "What to look for (and what to avoid)",
                "What to do next"
              ].map((item) => (
                <li key={item} className="flex items-start gap-3" style={{ color: '#5A6B63' }}>
                  <Check className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#34694D' }} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SECTION 4 — HOW IT WORKS */}
      <section id="how-it-works" className="py-24 px-4" style={{ backgroundColor: '#1E3F30' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl text-center mb-4 text-white">
            Clarity in three simple steps
          </h2>
          <p className="text-center mb-16 text-white/60 text-sm">
            No guesswork. No overwhelm. Just direction.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { num: "1", title: "Tell us about your parent", desc: "Answer a few simple questions about their situation, health, and your timeline." },
              { num: "2", title: "We analyze your situation", desc: "Our system processes your inputs against provincial care data and requirements." },
              { num: "3", title: "Get your personalized CareMap", desc: "Receive clear recommendations, costs, and next steps tailored to your family." }
            ].map((step) => (
              <div key={step.num} className="text-center">
                <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-6" style={{ backgroundColor: 'rgba(255,255,255,0.1)', border: '2px solid rgba(255,255,255,0.3)' }}>
                  <span className="font-heading text-2xl text-white">{step.num}</span>
                </div>
                <h3 className="font-heading text-xl mb-3 text-white">{step.title}</h3>
                <p className="text-white/70">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — OUTPUT */}
      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl text-center mb-16" style={{ color: '#232F2A' }}>
            Your personalized CareMap includes:
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: MapPin, title: "Recommended care path", desc: "The right type of care for your parent's needs" },
              { icon: DollarSign, title: "Estimated monthly costs", desc: "Province-specific pricing you can plan around" },
              { icon: Shield, title: "Coverage insights", desc: "What's covered vs. out-of-pocket" },
              { icon: AlertTriangle, title: "Red flags to watch", desc: "What to avoid when evaluating facilities" },
              { icon: HelpCircle, title: "Questions checklist", desc: "What to ask during facility tours" },
              { icon: ChevronRight, title: "Next-step plan", desc: "Clear actions to take starting today" }
            ].map((item) => (
              <div key={item.title} className="card">
                <item.icon className="w-8 h-8 mb-4" style={{ color: '#1E3F30' }} strokeWidth={1.5} />
                <h3 className="font-heading text-lg mb-2" style={{ color: '#232F2A' }}>{item.title}</h3>
                <p className="text-sm" style={{ color: '#5A6B63' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section with Rotating Diverse Images */}
      <TrustSection />

      {/* SECTION 6 — TRUST & DISCLAIMER */}
      <section className="py-24 px-4" style={{ backgroundColor: '#E6EDE8' }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl mb-8" style={{ color: '#232F2A' }}>
            Built for real decisions.<br />Not generic advice.
          </h2>
          <p className="text-lg mb-8" style={{ color: '#5A6B63' }}>
            CareMap Canada is designed around how elder care actually works across Canadian provinces.
          </p>
          <div className="card inline-block text-left">
            <p className="text-sm" style={{ color: '#5A6B63' }}>
              <strong style={{ color: '#232F2A' }}>Important:</strong> This tool provides general guidance based on your inputs. Care eligibility and coverage vary by province and individual circumstances. Always confirm with a licensed healthcare professional.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 7 — CONVERSION CLOSE */}
      <section className="py-24 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6" style={{ color: '#232F2A' }}>
            You've already been carrying this alone.
          </h2>
          <p className="text-xl mb-10" style={{ color: '#5A6B63' }}>
            You don't have to anymore.
          </p>
          <button 
            onClick={() => navigate("/assessment")}
            className="btn-primary text-lg px-10 py-4 flex items-center justify-center gap-2 mx-auto"
            data-testid="final-cta-btn"
          >
            Get Your CareMap
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t" style={{ borderColor: '#D5DFD9' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div>
              <h3 className="font-heading text-xl" style={{ color: '#1E3F30' }}>CareMap Canada™</h3>
              <p className="text-sm" style={{ color: '#5A6B63' }}>Your guide through elder care decisions.</p>
            </div>
            <div className="flex flex-wrap justify-center gap-6">
              <button onClick={() => setShowPricingModal(true)} className="text-sm hover:underline bg-transparent border-0 cursor-pointer" style={{ color: '#5A6B63' }} data-testid="footer-provider-link">Provider Directory</button>
              <button onClick={() => setShowPricingModal(true)} className="text-sm hover:underline bg-transparent border-0 cursor-pointer" style={{ color: '#5A6B63' }} data-testid="footer-compare-link">Compare Options</button>
              <button onClick={() => setShowPricingModal(true)} className="text-sm hover:underline bg-transparent border-0 cursor-pointer" style={{ color: '#5A6B63' }} data-testid="footer-community-link">Community</button>
              <button onClick={() => setShowPricingModal(true)} className="text-sm hover:underline bg-transparent border-0 cursor-pointer" style={{ color: '#5A6B63' }} data-testid="footer-coordinator-link">Find a Coordinator</button>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-4 mt-6">
            <Link to="/terms" className="text-xs hover:underline" style={{ color: '#5A6B63' }} data-testid="footer-terms-link">Terms of Service</Link>
            <span className="text-xs" style={{ color: '#D5DFD9' }}>|</span>
            <Link to="/privacy" className="text-xs hover:underline" style={{ color: '#5A6B63' }} data-testid="footer-privacy-link">Privacy Policy</Link>
          </div>
          <p className="text-center text-xs mt-4" style={{ color: '#5A6B63' }}>
            © 2026 CareMap Canada. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Exit Intent Modal */}
      {showExitIntent && (
        <div className="modal-overlay" onClick={() => setShowExitIntent(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} data-testid="exit-intent-modal">
            <button 
              onClick={() => setShowExitIntent(false)} 
              className="absolute top-4 right-4"
              data-testid="close-exit-modal-btn"
            >
              <X className="w-6 h-6" style={{ color: '#5A6B63' }} />
            </button>
            <div className="text-center">
              <h3 className="font-heading text-2xl mb-3" style={{ color: '#232F2A' }}>
                Before you go...
              </h3>
              <p className="mb-2" style={{ color: '#5A6B63' }}>
                You don't have to figure this out alone.
              </p>
              <p className="mb-6 text-sm" style={{ color: '#5A6B63' }}>
                Get 20% off your first Pro report with code <strong style={{ color: '#1E3F30' }}>CARE20</strong>
              </p>
              <button 
                onClick={() => { setShowExitIntent(false); navigate("/assessment"); }}
                className="btn-primary w-full"
                data-testid="exit-discount-btn"
              >
                Get Your CareMap
              </button>
            </div>
          </div>
        </div>
      )}

      <PricingModal show={showPricingModal} onClose={() => setShowPricingModal(false)} />
    </div>
  );
};

// Assessment Form Component
const Assessment = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const user = auth?.user;
  const [step, setStep] = useState(1);
  const [sessionId] = useState(() => generateSessionId());
  const [provinces, setProvinces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStage, setLoadingStage] = useState("");
  const [reportError, setReportError] = useState("");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState("");
  
  const [formData, setFormData] = useState({
    // Step 1: Parent Profile
    age: "",
    province: "",
    living_situation: "",
    // Step 2: Health Condition
    primary_conditions: [],
    dementia_stage: "",
    requires_supervision: false,
    // Step 3: Functional Ability
    mobility_level: "",
    daily_activities: "",
    cognitive_function: "",
    // Step 4: Financial Snapshot
    monthly_income: "",
    has_savings: false,
    owns_home: false,
    has_insurance: false,
    // Step 5: Urgency
    urgency: "",
    crisis_factors: []
  });

  useEffect(() => {
    const fetchProvinces = async () => {
      try {
        const res = await axios.get(`${API}/provinces`);
        setProvinces(res.data.provinces);
      } catch (e) {
        console.error("Error fetching provinces:", e);
      }
    };
    fetchProvinces();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only fetch; API and axios are stable module-level constants

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayItem = (field, item) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(item) 
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  const saveProgress = async () => {
    try {
      await axios.post(`${API}/assessment/save`, {
        session_id: sessionId,
        step,
        data: formData
      });
    } catch (e) {
      console.error("Error saving progress:", e);
    }
  };

  const nextStep = async () => {
    await saveProgress();
    if (step < 5) {
      setStep(step + 1);
    } else {
      setShowEmailModal(true);
    }
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const generateReport = async () => {
    setLoading(true);
    setReportError("");
    setLoadingProgress(0);
    setLoadingStage("Preparing your assessment...");
    
    // Simulated progress — increases over time while API call runs
    const stages = [
      { at: 5, text: "Analyzing care needs..." },
      { at: 20, text: "Evaluating province-specific options..." },
      { at: 35, text: "Generating personalized recommendations..." },
      { at: 55, text: "Building your cost breakdown..." },
      { at: 70, text: "Compiling your CareMap report..." },
      { at: 85, text: "Finalizing details..." },
    ];
    let progress = 0;
    const progressInterval = setInterval(() => {
      progress += Math.random() * 4 + 1;
      if (progress > 92) progress = 92; // Never hit 100 until done
      setLoadingProgress(Math.round(progress));
      const stage = [...stages].reverse().find(s => progress >= s.at);
      if (stage) setLoadingStage(stage.text);
    }, 500);

    try {
      if (email) {
        try {
          await axios.post(`${API}/email/capture`, { email, session_id: sessionId });
        } catch (emailErr) {
          console.warn("Email capture failed (non-blocking):", emailErr.message);
        }
      }
      
      const assessmentPayload = {
        parent_profile: {
          age: parseInt(formData.age) || 75,
          province: formData.province,
          living_situation: formData.living_situation
        },
        health_condition: {
          primary_conditions: formData.primary_conditions,
          dementia_stage: formData.dementia_stage || null,
          requires_supervision: formData.requires_supervision
        },
        functional_ability: {
          mobility_level: formData.mobility_level,
          daily_activities: formData.daily_activities,
          cognitive_function: formData.cognitive_function
        },
        financial_snapshot: {
          monthly_income: formData.monthly_income,
          has_savings: formData.has_savings,
          owns_home: formData.owns_home,
          has_insurance: formData.has_insurance
        },
        urgency_level: {
          urgency: formData.urgency,
          crisis_factors: formData.crisis_factors
        }
      };

      let res;
      try {
        res = await axios.post(`${API}/generate-report`, assessmentPayload, { timeout: 40000 });
      } catch (firstErr) {
        console.warn("First attempt failed, retrying...", firstErr.message);
        setLoadingStage("Retrying... almost there...");
        res = await axios.post(`${API}/generate-report`, assessmentPayload, { timeout: 40000 });
      }
      
      clearInterval(progressInterval);
      setLoadingProgress(100);
      setLoadingStage("Complete!");
      
      setShowEmailModal(false);
      
      // Auto-save assessment to user's profile if logged in
      if (user) {
        try {
          await axios.post(`${API}/assessment/save-full`, {
            email: user.email,
            name: `CareMap Report — ${res.data.province || formData.province}`,
            assessment_data: assessmentPayload,
            report_id: res.data.id
          });
        } catch (saveErr) {
          console.warn("Auto-save failed (may have reached free tier limit):", saveErr.message);
        }
      }
      
      setTimeout(() => {
        navigate("/report", { state: { report: res.data, sessionId, formData } });
      }, 400);
    } catch (e) {
      clearInterval(progressInterval);
      console.error("Error generating report:", e);
      setReportError("There was an issue generating your report. Please try again.");
    } finally {
      setLoading(false);
      setLoadingProgress(0);
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1: return formData.age && formData.province && formData.living_situation;
      case 2: return formData.primary_conditions.length > 0;
      case 3: return formData.mobility_level && formData.daily_activities && formData.cognitive_function;
      case 4: return formData.monthly_income;
      case 5: return formData.urgency;
      default: return false;
    }
  };

  const progress = (step / 5) * 100;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      {/* Header with Logo */}
      <div className="sticky top-0 z-50 glass border-b" style={{ borderColor: '#D5DFD9' }}>
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex flex-col">
              <span className="font-heading text-lg" style={{ color: '#1E3F30' }}>CareMap Canada™</span>
            </Link>
            <div className="hidden sm:flex items-center gap-4">
              <SocialProof />
            </div>
          </div>
        </div>
      </div>
      
      {/* Background */}
      <div 
        className="fixed inset-0 bg-cover bg-center opacity-10 pointer-events-none"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1516733968668-dbdce39c4651?w=1600')` }}
      />
      
      <div className="relative max-w-2xl mx-auto px-4 py-6 sm:py-8">
        {/* Assessment Title - visible on mobile */}
        <div className="text-center mb-6">
          <h1 className="font-heading text-xl sm:text-2xl mb-1" style={{ color: '#232F2A' }}>Your Personal Care Assessment</h1>
          <p className="text-sm" style={{ color: '#5A6B63' }}>Answer a few questions to receive your personalized CareMap report</p>
        </div>

        {/* Progress Bar with Start Over */}
        <div className="mb-8">
          <div className="flex justify-between items-center text-sm mb-2">
            <div className="flex items-center gap-4">
              <span style={{ color: '#5A6B63' }}>Step {step} of 5</span>
              {step > 1 && (
                <Link 
                  to="/" 
                  className="text-xs underline hover:no-underline" 
                  style={{ color: '#5A6B63' }}
                  data-testid="start-over-link"
                >
                  Start over
                </Link>
              )}
            </div>
            <span style={{ color: '#5A6B63' }}>{Math.round(progress)}% Complete</span>
          </div>
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${progress}%` }} data-testid="progress-bar" />
          </div>
        </div>

        {/* Step Content */}
        <div className="card animate-fadeIn">
          {step === 1 && (
            <div data-testid="step-1-parent-profile">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E6EDE8' }}>
                  <Users className="w-6 h-6" style={{ color: '#1E3F30' }} strokeWidth={1.5} />
                </div>
                <div>
                  <h2 className="font-heading text-2xl" style={{ color: '#232F2A' }}>Parent Profile</h2>
                  <p style={{ color: '#5A6B63' }}>Let's start with some basic information</p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#232F2A' }}>Parent's Age</label>
                  <input 
                    type="number" 
                    min="60" 
                    max="110"
                    value={formData.age}
                    onChange={e => updateForm("age", e.target.value)}
                    className="input-base"
                    placeholder="Enter age"
                    data-testid="age-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#232F2A' }}>Province</label>
                  <select 
                    value={formData.province}
                    onChange={e => updateForm("province", e.target.value)}
                    className="input-base"
                    data-testid="province-select"
                  >
                    <option value="">Select province</option>
                    {provinces.map(p => (
                      <option key={p.code} value={p.code}>{p.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-3" style={{ color: '#232F2A' }}>Current Living Situation</label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: "alone", label: "Living Alone", icon: Home },
                      { value: "with_spouse", label: "With Spouse", icon: Heart },
                      { value: "with_family", label: "With Family", icon: Users },
                      { value: "facility", label: "In Facility", icon: Building2 }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => updateForm("living_situation", opt.value)}
                        className={`option-card relative flex flex-col items-center gap-2 p-4 ${formData.living_situation === opt.value ? 'selected' : ''}`}
                        data-testid={`living-${opt.value}`}
                      >
                        <opt.icon className="w-6 h-6" style={{ color: '#1E3F30' }} strokeWidth={1.5} />
                        <span className="text-sm text-center" style={{ color: '#232F2A' }}>{opt.label}</span>
                        {formData.living_situation === opt.value && (
                          <div className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1E3F30' }}>
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-2-health">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E6EDE8' }}>
                  <Activity className="w-6 h-6" style={{ color: '#1E3F30' }} strokeWidth={1.5} />
                </div>
                <div>
                  <h2 className="font-heading text-2xl" style={{ color: '#232F2A' }}>Health Conditions</h2>
                  <p style={{ color: '#5A6B63' }}>Select all that apply</p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-3" style={{ color: '#232F2A' }}>Primary Health Concerns</label>
                  <div className="space-y-2">
                    {[
                      { value: "dementia", label: "Dementia / Alzheimer's" },
                      { value: "heart_disease", label: "Heart Disease" },
                      { value: "diabetes", label: "Diabetes" },
                      { value: "mobility", label: "Mobility Issues" },
                      { value: "vision_hearing", label: "Vision/Hearing Loss" },
                      { value: "other", label: "Other Chronic Conditions" }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => toggleArrayItem("primary_conditions", opt.value)}
                        className={`option-card w-full text-left flex items-center justify-between ${formData.primary_conditions.includes(opt.value) ? 'selected' : ''}`}
                        data-testid={`condition-${opt.value}`}
                      >
                        <span style={{ color: '#232F2A' }}>{opt.label}</span>
                        {formData.primary_conditions.includes(opt.value) && (
                          <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1E3F30' }}>
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {formData.primary_conditions.includes("dementia") && (
                  <div className="animate-fadeIn">
                    <label className="block text-sm font-medium mb-3" style={{ color: '#232F2A' }}>Dementia Stage</label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { value: "early", label: "Early" },
                        { value: "moderate", label: "Moderate" },
                        { value: "advanced", label: "Advanced" }
                      ].map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => updateForm("dementia_stage", opt.value)}
                          className={`option-card text-center ${formData.dementia_stage === opt.value ? 'selected' : ''}`}
                          data-testid={`dementia-${opt.value}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <button
                    onClick={() => updateForm("requires_supervision", !formData.requires_supervision)}
                    className={`option-card w-full text-left flex items-center justify-between ${formData.requires_supervision ? 'selected' : ''}`}
                    data-testid="supervision-toggle"
                  >
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5" style={{ color: '#1E3F30' }} strokeWidth={1.5} />
                      <span style={{ color: '#232F2A' }}>Requires 24/7 Supervision</span>
                    </div>
                    {formData.requires_supervision && (
                      <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1E3F30' }}>
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-3-functional">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E6EDE8' }}>
                  <Activity className="w-6 h-6" style={{ color: '#1E3F30' }} strokeWidth={1.5} />
                </div>
                <div>
                  <h2 className="font-heading text-2xl" style={{ color: '#232F2A' }}>Functional Ability</h2>
                  <p style={{ color: '#5A6B63' }}>Help us understand daily capabilities</p>
                </div>
              </div>

              <div className="space-y-6">
                {[
                  { 
                    field: "mobility_level", 
                    label: "Mobility", 
                    options: [
                      { value: "independent", label: "Fully Independent" },
                      { value: "some_help", label: "Needs Some Help" },
                      { value: "significant_help", label: "Significant Help" },
                      { value: "fully_dependent", label: "Fully Dependent" }
                    ]
                  },
                  { 
                    field: "daily_activities", 
                    label: "Daily Activities (eating, bathing, dressing)", 
                    options: [
                      { value: "independent", label: "Fully Independent" },
                      { value: "some_help", label: "Needs Some Help" },
                      { value: "significant_help", label: "Significant Help" },
                      { value: "fully_dependent", label: "Fully Dependent" }
                    ]
                  },
                  { 
                    field: "cognitive_function", 
                    label: "Cognitive Function", 
                    options: [
                      { value: "sharp", label: "Sharp / Clear" },
                      { value: "mild_decline", label: "Mild Decline" },
                      { value: "moderate_decline", label: "Moderate Decline" },
                      { value: "severe_decline", label: "Severe Decline" }
                    ]
                  }
                ].map((section, idx) => (
                  <div key={section.field}>
                    <label className="block text-sm font-medium mb-3" style={{ color: '#232F2A' }}>{section.label}</label>
                    <div className="grid grid-cols-2 gap-2">
                      {section.options.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => updateForm(section.field, opt.value)}
                          className={`option-card text-center py-3 ${formData[section.field] === opt.value ? 'selected' : ''}`}
                          data-testid={`${section.field}-${opt.value}`}
                        >
                          <span className="text-sm" style={{ color: '#232F2A' }}>{opt.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="step-4-financial">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E6EDE8' }}>
                  <DollarSign className="w-6 h-6" style={{ color: '#1E3F30' }} strokeWidth={1.5} />
                </div>
                <div>
                  <h2 className="font-heading text-2xl" style={{ color: '#232F2A' }}>Financial Snapshot</h2>
                  <p style={{ color: '#5A6B63' }}>This helps us identify subsidy options</p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-3" style={{ color: '#232F2A' }}>Monthly Income (approximate)</label>
                  <div className="space-y-2">
                    {[
                      { value: "under_2000", label: "Under $2,000" },
                      { value: "2000_4000", label: "$2,000 - $4,000" },
                      { value: "4000_6000", label: "$4,000 - $6,000" },
                      { value: "over_6000", label: "Over $6,000" }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => updateForm("monthly_income", opt.value)}
                        className={`option-card w-full text-left ${formData.monthly_income === opt.value ? 'selected' : ''}`}
                        data-testid={`income-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  {[
                    { field: "has_savings", label: "Has Significant Savings" },
                    { field: "owns_home", label: "Owns Home/Property" },
                    { field: "has_insurance", label: "Has Long-Term Care Insurance" }
                  ].map(opt => (
                    <button
                      key={opt.field}
                      onClick={() => updateForm(opt.field, !formData[opt.field])}
                      className={`option-card w-full text-left flex items-center justify-between ${formData[opt.field] ? 'selected' : ''}`}
                      data-testid={`financial-${opt.field}`}
                    >
                      <span style={{ color: '#232F2A' }}>{opt.label}</span>
                      {formData[opt.field] && (
                        <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1E3F30' }}>
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div data-testid="step-5-urgency">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E6EDE8' }}>
                  <Clock className="w-6 h-6" style={{ color: '#1E3F30' }} strokeWidth={1.5} />
                </div>
                <div>
                  <h2 className="font-heading text-2xl" style={{ color: '#232F2A' }}>Urgency Level</h2>
                  <p style={{ color: '#5A6B63' }}>When do you need care in place?</p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-3" style={{ color: '#232F2A' }}>Timeline</label>
                  <div className="space-y-2">
                    {[
                      { value: "planning_ahead", label: "Planning Ahead (1+ years)", desc: "No immediate need" },
                      { value: "within_year", label: "Within the Year", desc: "Starting to explore options" },
                      { value: "within_months", label: "Within Months", desc: "Need to act soon" },
                      { value: "immediate", label: "Immediate", desc: "Crisis or urgent situation" }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => updateForm("urgency", opt.value)}
                        className={`option-card w-full text-left ${formData.urgency === opt.value ? 'selected' : ''}`}
                        data-testid={`urgency-${opt.value}`}
                      >
                        <div className="font-medium" style={{ color: '#232F2A' }}>{opt.label}</div>
                        <div className="text-sm" style={{ color: '#5A6B63' }}>{opt.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-3" style={{ color: '#232F2A' }}>Any Recent Crisis Factors?</label>
                  <div className="space-y-2">
                    {[
                      { value: "recent_fall", label: "Recent Fall" },
                      { value: "hospitalization", label: "Recent Hospitalization" },
                      { value: "caregiver_burnout", label: "Caregiver Burnout" },
                      { value: "safety_concern", label: "Safety Concerns at Home" }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => toggleArrayItem("crisis_factors", opt.value)}
                        className={`option-card w-full text-left flex items-center justify-between ${formData.crisis_factors.includes(opt.value) ? 'selected' : ''}`}
                        data-testid={`crisis-${opt.value}`}
                      >
                        <span style={{ color: '#232F2A' }}>{opt.label}</span>
                        {formData.crisis_factors.includes(opt.value) && (
                          <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1E3F30' }}>
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <div className="mt-6 p-4 rounded-xl" style={{ backgroundColor: '#E6EDE8' }}>
            <p className="text-xs flex items-start gap-2" style={{ color: '#5A6B63' }}>
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              This tool provides general guidance only. It is not medical, financial, or legal advice.
            </p>
          </div>

          {/* Navigation */}
          <div className="flex justify-between mt-8">
            {step > 1 ? (
              <button onClick={prevStep} className="btn-secondary flex items-center gap-2" data-testid="prev-btn">
                <ChevronLeft className="w-5 h-5" />
                Back
              </button>
            ) : (
              <div />
            )}
            <button 
              onClick={nextStep} 
              disabled={!isStepValid()}
              className={`btn-primary flex items-center gap-2 ${!isStepValid() ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid="next-btn"
            >
              {step === 5 ? "Generate Report" : "Continue"}
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Email Capture Modal */}
        {showEmailModal && (
          <div className="modal-overlay" data-testid="email-modal">
            <div className="modal-content glass">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4" style={{ backgroundColor: '#E6EDE8' }}>
                  <Mail className="w-8 h-8" style={{ color: '#1E3F30' }} />
                </div>
                <h3 className="font-heading text-2xl mb-2" style={{ color: '#232F2A' }}>
                  Save Your Results
                </h3>
                <p className="mb-6" style={{ color: '#5A6B63' }}>
                  Enter your email to receive a copy of your personalized CareMap report.
                </p>
                <input 
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="input-base mb-4"
                  data-testid="email-input"
                />
                <div className="flex flex-col gap-3">
                  {reportError && (
                    <div className="flex items-center gap-2 p-3 rounded-lg text-sm" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }} data-testid="report-error">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      {reportError}
                    </div>
                  )}
                  {loading ? (
                    <div className="w-full" data-testid="progress-bar-container">
                      <div className="w-full h-3 rounded-full overflow-hidden mb-2" style={{ backgroundColor: '#E6EDE8' }}>
                        <div 
                          className="h-full rounded-full transition-all duration-500 ease-out"
                          style={{ width: `${loadingProgress}%`, backgroundColor: '#1E3F30' }}
                          data-testid="progress-bar"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-sm" style={{ color: '#5A6B63' }}>{loadingStage}</p>
                        <p className="text-sm font-medium" style={{ color: '#1E3F30' }}>{loadingProgress}%</p>
                      </div>
                    </div>
                  ) : (
                    <button 
                      onClick={generateReport} 
                      className="btn-primary w-full flex items-center justify-center gap-2"
                      data-testid="generate-report-btn"
                    >
                      Generate My CareMap
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  )}
                  {!loading && (
                    <button 
                      onClick={() => { setEmail(""); generateReport(); }}
                      className="text-sm underline"
                      style={{ color: '#5A6B63' }}
                      data-testid="skip-email-btn"
                    >
                      Continue Without Email
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Report Page Component
const ReportPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [showPricing, setShowPricing] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [userTier, setUserTier] = useState("free");

  useEffect(() => {
    // Get report from navigation state using useLocation
    const state = location.state;
    if (state?.report) {
      setReport(state.report);
      setSessionId(state.sessionId);
    } else {
      navigate("/assessment");
    }

    // Fetch pricing
    const fetchPricing = async () => {
      try {
        const res = await axios.get(`${API}/pricing`);
        setPricing(res.data.packages);
      } catch (e) {
        console.error("Error fetching pricing:", e);
      }
    };
    fetchPricing();
  }, [navigate, location.state]); // eslint-disable-line react-hooks/exhaustive-deps -- API and axios are stable module-level constants

  const handleUpgrade = async (packageId) => {
    if (packageId === "free") {
      setUserTier("free");
      setShowPricing(false);
      return;
    }
    
    try {
      const res = await axios.post(`${API}/checkout/create`, {
        package_id: packageId,
        session_id: sessionId,
        origin_url: window.location.origin,
        user_email: user?.email || null
      });
      window.location.href = res.data.url;
    } catch (e) {
      console.error("Checkout error:", e);
    }
  };

  const exportPDF = async () => {
    const html2pdf = (await import('html2pdf.js')).default;
    
    // Build branded cover page
    const coverPage = document.createElement('div');
    coverPage.innerHTML = `
      <div style="page-break-after: always; min-height: 100vh; display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center; font-family: 'Lora', Georgia, serif; padding: 60px 40px; box-sizing: border-box; background: linear-gradient(180deg, #FDFBF7 0%, #E6EDE8 100%);">
        <div style="flex: 1; display: flex; flex-direction: column; justify-content: center; align-items: center;">
          <div style="width: 80px; height: 80px; border-radius: 50%; background: #1E3F30; display: flex; align-items: center; justify-content: center; margin-bottom: 24px;">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
          </div>
          <h1 style="font-size: 36px; color: #1E3F30; margin: 0 0 8px; font-weight: 700; letter-spacing: -0.5px;">CareMap Canada&#8482;</h1>
          <p style="font-size: 14px; color: #5A6B63; margin: 0 0 40px; letter-spacing: 2px; text-transform: uppercase;">Your Personalized Elder Care Report</p>
          
          <div style="width: 60px; height: 2px; background: #1E3F30; margin: 0 auto 40px;"></div>
          
          <div style="background: white; border-radius: 16px; padding: 32px 40px; max-width: 400px; width: 100%; box-shadow: 0 4px 20px rgba(30,63,48,0.08);">
            <div style="margin-bottom: 20px;">
              <p style="font-size: 11px; color: #5A6B63; text-transform: uppercase; letter-spacing: 1px; margin: 0 0 4px;">Province</p>
              <p style="font-size: 18px; color: #232F2A; margin: 0; font-weight: 600;">${report.province || 'Ontario'}</p>
            </div>
            <div style="margin-bottom: 20px;">
              <p style="font-size: 11px; color: #5A6B63; text-transform: uppercase; letter-spacing: 1px; margin: 0 0 4px;">Recommended Care Path</p>
              <p style="font-size: 18px; color: #1E3F30; margin: 0; font-weight: 600;">${formatCareType(report.care_path?.primary || '')}</p>
            </div>
            <div>
              <p style="font-size: 11px; color: #5A6B63; text-transform: uppercase; letter-spacing: 1px; margin: 0 0 4px;">Date Generated</p>
              <p style="font-size: 16px; color: #232F2A; margin: 0;">${new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
          </div>
        </div>
        
        <div style="margin-top: 40px;">
          <p style="font-size: 13px; color: #5A6B63; margin: 0 0 6px; font-style: italic;">
            &ldquo;You are not alone in this journey. Every step you take for your loved one matters.&rdquo;
          </p>
          <p style="font-size: 11px; color: #5A6B63; margin: 0;">www.caremapcanada.com</p>
        </div>
      </div>
    `;

    // Combine cover + report content
    const combined = document.createElement('div');
    combined.appendChild(coverPage);
    
    const reportClone = document.getElementById("report-content").cloneNode(true);
    combined.appendChild(reportClone);
    
    document.body.appendChild(combined);

    const opt = {
      margin: [5, 5, 10, 5],
      filename: `CareMap_Report_${(report.province || 'Canada').replace(/\s/g, '_')}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };

    await html2pdf().set(opt).from(combined).save();
    document.body.removeChild(combined);
  };

  const downloadChecklist = async () => {
    setChecklistLoading(true);
    try {
      const res = await axios.post(`${API}/checklist/generate`, {
        care_path: report.care_path.primary,
        province_code: report.province_code,
        subsidy_eligible: report.subsidy_eligible,
        user_tier: user?.tier || "free"
      });
      const cl = res.data;

      // Build HTML for the checklist PDF
      const checkboxStyle = `display:inline-block;width:16px;height:16px;border:2px solid #1E3F30;border-radius:3px;margin-right:10px;vertical-align:middle;flex-shrink:0;`;
      const renderItems = (items) => items.map(item => 
        `<div style="display:flex;align-items:flex-start;gap:4px;margin-bottom:10px;page-break-inside:avoid;">
          <div style="${checkboxStyle}"></div>
          <span style="font-size:13px;color:#232F2A;line-height:1.5;">${item}</span>
        </div>`
      ).join("");

      const html = `
        <div style="font-family:'Lora',Georgia,serif;max-width:700px;margin:0 auto;padding:40px 30px;">
          <div style="text-align:center;margin-bottom:30px;border-bottom:2px solid #1E3F30;padding-bottom:20px;">
            <h1 style="font-size:24px;color:#1E3F30;margin:0 0 6px;">CareMap Canada</h1>
            <h2 style="font-size:20px;color:#232F2A;margin:0 0 6px;">${cl.title}</h2>
            <p style="font-size:13px;color:#5A6B63;margin:0;">Personalized for ${cl.province} | ${formatCareType(cl.care_path)}</p>
          </div>

          <div style="margin-bottom:28px;">
            <div style="background:#1E3F30;color:white;padding:8px 16px;border-radius:6px;margin-bottom:14px;">
              <h3 style="margin:0;font-size:15px;">This Week</h3>
            </div>
            ${renderItems(cl.this_week)}
          </div>

          <div style="margin-bottom:28px;">
            <div style="background:#34694D;color:white;padding:8px 16px;border-radius:6px;margin-bottom:14px;">
              <h3 style="margin:0;font-size:15px;">This Month</h3>
            </div>
            ${renderItems(cl.this_month)}
          </div>

          <div style="margin-bottom:28px;">
            <div style="background:#5A6B63;color:white;padding:8px 16px;border-radius:6px;margin-bottom:14px;">
              <h3 style="margin:0;font-size:15px;">Next 3 Months</h3>
            </div>
            ${renderItems(cl.next_3_months)}
          </div>

          ${cl.pro_subsidy_steps && cl.pro_subsidy_steps.length > 0 ? `
          <div style="margin-bottom:28px;">
            <div style="background:#C26D5C;color:white;padding:8px 16px;border-radius:6px;margin-bottom:14px;">
              <h3 style="margin:0;font-size:15px;">Subsidy & Financial Steps (Pro)</h3>
            </div>
            ${renderItems(cl.pro_subsidy_steps)}
          </div>` : ''}

          <div style="margin-bottom:28px;">
            <div style="background:#E6EDE8;padding:8px 16px;border-radius:6px;margin-bottom:14px;">
              <h3 style="margin:0;font-size:15px;color:#1E3F30;">Questions to Ask Care Providers</h3>
            </div>
            ${cl.questions_for_providers.map((q, i) => 
              `<div style="display:flex;align-items:flex-start;gap:4px;margin-bottom:10px;">
                <span style="font-size:13px;color:#1E3F30;font-weight:600;min-width:20px;">${i+1}.</span>
                <span style="font-size:13px;color:#232F2A;line-height:1.5;">${q}</span>
              </div>`
            ).join("")}
          </div>

          <div style="border-top:1px solid #E6EDE8;padding-top:16px;margin-top:20px;">
            <p style="font-size:11px;color:#5A6B63;line-height:1.6;">${cl.disclaimer}</p>
            <p style="font-size:11px;color:#5A6B63;margin-top:6px;">Generated by CareMap Canada — www.caremapcanada.com</p>
          </div>
        </div>
      `;

      // Create temp element and generate PDF
      const container = document.createElement('div');
      container.innerHTML = html;
      document.body.appendChild(container);

      const html2pdf = (await import('html2pdf.js')).default;
      await html2pdf().set({
        margin: [10, 10, 10, 10],
        filename: `CareMap_Checklist_${cl.province.replace(/\s/g,'_')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      }).from(container).save();

      document.body.removeChild(container);
    } catch (e) {
      console.error("Checklist error:", e);
    } finally {
      setChecklistLoading(false);
    }
  };

  const [saved, setSaved] = useState(!!user);
  const [saveError, setSaveError] = useState("");
  const [checklistLoading, setChecklistLoading] = useState(false);

  const saveAssessment = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    try {
      await axios.post(`${API}/assessment/save-full`, {
        email: user.email,
        name: `CareMap Report — ${report.province}`,
        assessment_data: location.state?.formData || {},
        report_id: report.id
      });
      setSaved(true);
      setSaveError("");
    } catch (e) {
      const msg = e.response?.data?.detail || "Error saving assessment";
      setSaveError(msg);
    }
  };

  if (!report) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FDFBF7' }}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#E6EDE8', borderTopColor: '#1E3F30' }} />
          <p style={{ color: '#5A6B63' }}>Loading your report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: '#FDFBF7' }}>
      {/* Navigation */}
      <Navigation />
      
      {/* Header with Back Button */}
      <div className="py-8" style={{ backgroundColor: '#1E3F30' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate("/dashboard")}
                className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
                data-testid="back-to-dashboard-btn"
              >
                <ChevronLeft className="w-5 h-5" />
                Dashboard
              </button>
              <div className="border-l border-white/30 pl-4">
                <h1 className="font-heading text-2xl sm:text-3xl text-white">Your CareMap Report</h1>
                <p className="text-white/70 text-sm mt-1">{report.province}</p>
              </div>
            </div>
            <div className="flex gap-3">
              {user && (
                <button 
                  onClick={saveAssessment} 
                  disabled={saved}
                  className={`btn-secondary flex items-center gap-2 text-sm ${saved ? 'opacity-60 cursor-default' : ''}`}
                  data-testid="save-assessment-btn"
                >
                  {saved ? <Check className="w-4 h-4" /> : <BookOpen className="w-4 h-4" />}
                  <span className="hidden sm:inline">{saved ? 'Saved' : 'Save'}</span>
                </button>
              )}
              <button onClick={exportPDF} className="btn-secondary flex items-center gap-2 text-sm" data-testid="export-pdf-btn">
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Export PDF</span>
              </button>
              <button 
                onClick={downloadChecklist} 
                disabled={checklistLoading}
                className="btn-secondary flex items-center gap-2 text-sm"
                data-testid="download-checklist-btn"
              >
                {checklistLoading ? (
                  <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: '#E6EDE8', borderTopColor: '#1E3F30' }} />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">Checklist</span>
              </button>
              <button onClick={() => setShowPricing(true)} className="btn-accent flex items-center gap-2 text-sm" data-testid="upgrade-btn">
                <Star className="w-4 h-4" />
                <span className="hidden sm:inline">Upgrade</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Save feedback */}
      {saveError && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="flex items-center gap-2 p-3 rounded-lg text-sm" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }} data-testid="save-error">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {saveError}
          </div>
        </div>
      )}
      {saved && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="flex items-center gap-2 p-3 rounded-lg text-sm" style={{ backgroundColor: '#ECFDF5', color: '#065F46' }} data-testid="save-success">
            <Check className="w-4 h-4 flex-shrink-0" />
            Assessment saved to your dashboard. <Link to="/dashboard" className="underline font-medium">View Dashboard</Link>
          </div>
        </div>
      )}

      {/* Quick Access - Free Features */}
      <div className="border-b" style={{ backgroundColor: '#E6EDE8', borderColor: '#D5DFD9' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 py-3">
            <span className="text-sm font-medium" style={{ color: '#232F2A' }}>Explore free features:</span>
            <Link to="/providers" state={{ province: report.province_code, category: carePathToCategory(report.care_path.primary) }} className="text-sm flex items-center gap-1.5 hover:underline" style={{ color: '#1E3F30' }} data-testid="report-providers-link">
              <Search className="w-4 h-4" />
              3 Provider Listings
            </Link>
            <Link to="/community" className="text-sm flex items-center gap-1.5 hover:underline" style={{ color: '#1E3F30' }}>
              <Users className="w-4 h-4" />
              Community (read-only)
            </Link>
            <button 
              onClick={() => document.getElementById('cost-breakdown')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-sm flex items-center gap-1.5 hover:underline"
              style={{ color: '#1E3F30' }}
            >
              <DollarSign className="w-4 h-4" />
              Province Costs
            </button>
          </div>
        </div>
      </div>

      {/* Report Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" id="report-content">
        {/* Care Path Card */}
        <div className="card mb-8 animate-fadeIn" data-testid="care-path-card">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#E6EDE8' }}>
              {report.care_path.primary === "memory_care" && <Brain className="w-8 h-8" style={{ color: '#1E3F30' }} strokeWidth={1.5} />}
              {report.care_path.primary === "long_term_care" && <Bed className="w-8 h-8" style={{ color: '#1E3F30' }} strokeWidth={1.5} />}
              {report.care_path.primary === "assisted_living" && <Building2 className="w-8 h-8" style={{ color: '#1E3F30' }} strokeWidth={1.5} />}
              {report.care_path.primary === "home_care" && <Home className="w-8 h-8" style={{ color: '#1E3F30' }} strokeWidth={1.5} />}
            </div>
            <div>
              <h2 className="font-heading text-2xl mb-1" style={{ color: '#232F2A' }}>
                Recommended: {report.care_path.name}
              </h2>
              <p style={{ color: '#5A6B63' }}>{report.care_path.reason}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="text-sm px-3 py-1 rounded-full" style={{ backgroundColor: '#E6EDE8', color: '#1E3F30' }}>
                  Alternatives: {report.care_path.alternatives?.map(formatCareType).join(", ") || "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bento Grid */}
        <div className="grid md:grid-cols-12 gap-6">
          {/* Cost Breakdown */}
          <div id="cost-breakdown" className="md:col-span-7 card animate-fadeIn stagger-1" data-testid="cost-breakdown">
            <h3 className="font-heading text-xl mb-4 flex items-center gap-2" style={{ color: '#232F2A' }}>
              <DollarSign className="w-5 h-5" />
              Cost Breakdown
            </h3>
            <div className="space-y-4">
              {Object.entries(report.cost_breakdown.all_options).map(([type, cost]) => (
                <div key={type} className="flex items-center justify-between p-3 rounded-xl" style={{ backgroundColor: type === report.care_path.primary ? '#E6EDE8' : 'transparent' }}>
                  <span style={{ color: '#232F2A' }}>{formatCareType(type)}</span>
                  <span className="font-medium" style={{ color: '#1E3F30' }}>${cost.toLocaleString()}/mo</span>
                </div>
              ))}
            </div>
            {userTier === "free" && (
              <div className="mt-4 p-4 rounded-xl text-center" style={{ backgroundColor: '#E6EDE8' }}>
                <p className="text-sm" style={{ color: '#5A6B63' }}>
                  Upgrade to Pro for detailed cost analysis and subsidy pathways
                </p>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="md:col-span-5 card animate-fadeIn stagger-2" data-testid="timeline-card">
            <h3 className="font-heading text-xl mb-4 flex items-center gap-2" style={{ color: '#232F2A' }}>
              <Clock className="w-5 h-5" />
              Timeline Expectations
            </h3>
            <div className="text-center py-6">
              <div className="text-5xl font-heading font-light mb-2" style={{ color: '#1E3F30' }}>
                {report.waitlist_estimate}
              </div>
              <p style={{ color: '#5A6B63' }}>months typical waitlist in {report.province}</p>
            </div>
            {report.subsidy_eligible && (
              <div className="p-4 rounded-xl" style={{ backgroundColor: '#34694D20' }}>
                <p className="text-sm flex items-center gap-2" style={{ color: '#34694D' }}>
                  <Check className="w-4 h-4" />
                  You may qualify for subsidies
                </p>
              </div>
            )}
          </div>

          {/* AI Recommendations */}
          <div className="md:col-span-12 card animate-fadeIn stagger-3" data-testid="ai-recommendations">
            <h3 className="font-heading text-xl mb-4 flex items-center gap-2" style={{ color: '#232F2A' }}>
              <Heart className="w-5 h-5" />
              Personalized Guidance
            </h3>
            <div 
              className="prose max-w-none ai-content" 
              style={{ color: '#5A6B63' }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(parseMarkdown(report.ai_recommendations)) }}
            />
          </div>

          {/* How This Was Calculated */}
          <div className="md:col-span-12 card animate-fadeIn stagger-4" data-testid="calculation-explanation">
            <h3 className="font-heading text-xl mb-4 flex items-center gap-2" style={{ color: '#232F2A' }}>
              <HelpCircle className="w-5 h-5" />
              How This Was Calculated
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl" style={{ backgroundColor: '#FDFBF7' }}>
                <h4 className="font-medium mb-2" style={{ color: '#232F2A' }}>Care Path Selection</h4>
                <p className="text-sm" style={{ color: '#5A6B63' }}>
                  Based on health conditions, functional ability, and supervision needs. We prioritize memory care for 
                  dementia with high supervision, long-term care for significant dependencies.
                </p>
              </div>
              <div className="p-4 rounded-xl" style={{ backgroundColor: '#FDFBF7' }}>
                <h4 className="font-medium mb-2" style={{ color: '#232F2A' }}>Cost Estimates</h4>
                <p className="text-sm" style={{ color: '#5A6B63' }}>
                  Based on 2025-2026 provincial health authority data and industry reports. Actual costs vary by facility, location, and level of care. 
                  Private rooms typically cost 20-40% more than shared accommodations.
                </p>
              </div>
              <div className="p-4 rounded-xl" style={{ backgroundColor: '#FDFBF7' }}>
                <h4 className="font-medium mb-2" style={{ color: '#232F2A' }}>Subsidy Eligibility</h4>
                <p className="text-sm" style={{ color: '#5A6B63' }}>
                  Based on income ranges. Final eligibility determined by provincial health authority assessment. 
                  These are estimates only.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 p-6 rounded-xl" style={{ backgroundColor: '#E6EDE8' }}>
          <p className="text-sm flex items-start gap-2" style={{ color: '#5A6B63' }}>
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            {report.disclaimer}
          </p>
        </div>
      </div>

      {/* Pricing Modal */}
      {showPricing && pricing && (
        <div className="modal-overlay" onClick={() => setShowPricing(false)} data-testid="pricing-modal">
          <div className="bg-white rounded-3xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading text-3xl" style={{ color: '#232F2A' }}>Choose Your Plan</h2>
              <button onClick={() => setShowPricing(false)} data-testid="close-pricing-btn">
                <X className="w-6 h-6" style={{ color: '#5A6B63' }} />
              </button>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {Object.entries(pricing).map(([id, pkg]) => (
                <div 
                  key={id} 
                  className={`card ${id === "pro" ? 'ring-2' : ''}`}
                  style={{ borderColor: id === "pro" ? '#1E3F30' : undefined }}
                  data-testid={`pricing-${id}`}
                >
                  {id === "pro" && (
                    <div className="text-xs font-medium px-3 py-1 rounded-full inline-block mb-4" style={{ backgroundColor: '#1E3F30', color: 'white' }}>
                      Most Popular
                    </div>
                  )}
                  <h3 className="font-heading text-2xl mb-2" style={{ color: '#232F2A' }}>{pkg.name}</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-heading" style={{ color: '#1E3F30' }}>
                      ${pkg.amount}
                    </span>
                    {pkg.amount > 0 && <span className="text-sm" style={{ color: '#5A6B63' }}> CAD</span>}
                  </div>
                  <ul className="space-y-2 mb-6">
                    {pkg.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm" style={{ color: '#5A6B63' }}>
                        <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#34694D' }} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button 
                    onClick={() => handleUpgrade(id)}
                    className={`w-full py-3 rounded-full font-medium transition-colors ${
                      id === "free" ? 'btn-secondary' : id === "premium" ? 'btn-accent' : 'btn-primary'
                    }`}
                    data-testid={`select-${id}-btn`}
                  >
                    {id === "free" ? "Continue Free" : `Get ${pkg.name}`}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const auth = useAuth();
  const user = auth?.user;
  const logout = auth?.logout;
  
  return (
    <>
    <nav className="glass border-b sticky top-0 z-50" style={{ borderColor: '#D5DFD9' }}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="font-heading text-xl" style={{ color: '#1E3F30' }}>
            CareMap Canada
          </Link>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-6">
            {user?.tier && user.tier !== "free" ? (
              <>
                <Link to="/providers" className="text-sm hover:opacity-70 transition-opacity" style={{ color: '#5A6B63' }}>
                  Provider Directory
                </Link>
                <Link to="/compare" className="text-sm hover:opacity-70 transition-opacity" style={{ color: '#5A6B63' }}>
                  Compare Options
                </Link>
                <Link to="/community" className="text-sm hover:opacity-70 transition-opacity" style={{ color: '#5A6B63' }}>
                  Community
                </Link>
                <Link to="/coordinators" className="text-sm hover:opacity-70 transition-opacity" style={{ color: '#5A6B63' }}>
                  Find a Coordinator
                </Link>
                <Link to="/articles" className="text-sm hover:opacity-70 transition-opacity" style={{ color: '#5A6B63' }} data-testid="nav-articles">
                  Articles
                </Link>
              </>
            ) : (
              <>
                <button onClick={() => setShowPricingModal(true)} className="text-sm hover:opacity-70 transition-opacity bg-transparent border-0 cursor-pointer" style={{ color: '#5A6B63' }}>
                  Provider Directory
                </button>
                <button onClick={() => setShowPricingModal(true)} className="text-sm hover:opacity-70 transition-opacity bg-transparent border-0 cursor-pointer" style={{ color: '#5A6B63' }}>
                  Compare Options
                </button>
                <button onClick={() => setShowPricingModal(true)} className="text-sm hover:opacity-70 transition-opacity bg-transparent border-0 cursor-pointer" style={{ color: '#5A6B63' }}>
                  Community
                </button>
                <button onClick={() => setShowPricingModal(true)} className="text-sm hover:opacity-70 transition-opacity bg-transparent border-0 cursor-pointer" style={{ color: '#5A6B63' }}>
                  Find a Coordinator
                </button>
                <Link to="/articles" className="text-sm hover:opacity-70 transition-opacity" style={{ color: '#5A6B63' }} data-testid="nav-articles-free">
                  Articles
                </Link>
              </>
            )}
            
            {user ? (
              <div className="flex items-center gap-4">
                <Link to="/dashboard" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                  <User className="w-4 h-4" style={{ color: '#1E3F30' }} />
                  <span className="text-sm" style={{ color: '#232F2A' }}>{user.name}</span>
                  {user.tier !== "free" && (
                    <span className="text-xs px-2 py-0.5 rounded-full uppercase" style={{ backgroundColor: user.tier === "premium" ? '#C26D5C' : '#1E3F30', color: 'white' }}>
                      {user.tier}
                    </span>
                  )}
                </Link>
                {user.role === "admin" && (
                  <Link to="/admin" className="text-xs px-2 py-1 rounded-full flex items-center gap-1 hover:opacity-80 transition-opacity" style={{ backgroundColor: '#1E3F30', color: 'white' }} data-testid="admin-panel-link">
                    <Settings className="w-3 h-3" />
                    Admin
                  </Link>
                )}
                <button 
                  onClick={logout}
                  className="text-sm flex items-center gap-1 hover:opacity-70"
                  style={{ color: '#5A6B63' }}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/faq" className="text-sm hover:opacity-70" style={{ color: '#5A6B63' }}>
                  FAQ
                </Link>
                <Link to="/login" className="text-sm hover:opacity-70" style={{ color: '#5A6B63' }}>
                  Sign In
                </Link>
                <Link to="/register" className="btn-primary text-sm py-2 px-4">
                  Sign Up
                </Link>
              </div>
            )}
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" style={{ color: '#1E3F30' }} />
          </button>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t" style={{ borderColor: '#D5DFD9' }}>
            <div className="flex flex-col gap-3">
              {user?.tier && user.tier !== "free" ? (
                <>
                  <Link to="/providers" className="px-2 py-2" style={{ color: '#5A6B63' }} onClick={() => setMobileMenuOpen(false)}>Provider Directory</Link>
                  <Link to="/compare" className="px-2 py-2" style={{ color: '#5A6B63' }} onClick={() => setMobileMenuOpen(false)}>Compare Options</Link>
                  <Link to="/community" className="px-2 py-2" style={{ color: '#5A6B63' }} onClick={() => setMobileMenuOpen(false)}>Community</Link>
                  <Link to="/coordinators" className="px-2 py-2" style={{ color: '#5A6B63' }} onClick={() => setMobileMenuOpen(false)}>Find a Coordinator</Link>
                </>
              ) : (
                <>
                  <button onClick={() => { setMobileMenuOpen(false); setShowPricingModal(true); }} className="px-2 py-2 text-left bg-transparent border-0 cursor-pointer" style={{ color: '#5A6B63' }}>Provider Directory</button>
                  <button onClick={() => { setMobileMenuOpen(false); setShowPricingModal(true); }} className="px-2 py-2 text-left bg-transparent border-0 cursor-pointer" style={{ color: '#5A6B63' }}>Compare Options</button>
                  <button onClick={() => { setMobileMenuOpen(false); setShowPricingModal(true); }} className="px-2 py-2 text-left bg-transparent border-0 cursor-pointer" style={{ color: '#5A6B63' }}>Community</button>
                  <button onClick={() => { setMobileMenuOpen(false); setShowPricingModal(true); }} className="px-2 py-2 text-left bg-transparent border-0 cursor-pointer" style={{ color: '#5A6B63' }}>Find a Coordinator</button>
                </>
              )}
              <Link to="/articles" className="px-2 py-2" style={{ color: '#5A6B63' }} onClick={() => setMobileMenuOpen(false)}>Articles</Link>
              {user ? (
                <>
                  <Link to="/dashboard" className="px-2 py-2 flex items-center gap-2" style={{ color: '#232F2A' }} onClick={() => setMobileMenuOpen(false)}>
                    <User className="w-4 h-4" />
                    {user.name} (Dashboard)
                    {user.tier !== "free" && (
                      <span className="text-xs px-2 py-0.5 rounded-full uppercase" style={{ backgroundColor: user.tier === "premium" ? '#C26D5C' : '#1E3F30', color: 'white' }}>
                        {user.tier}
                      </span>
                    )}
                  </Link>
                  {user.role === "admin" && (
                    <Link to="/admin" className="px-2 py-2 flex items-center gap-2" style={{ color: '#1E3F30' }} onClick={() => setMobileMenuOpen(false)}>
                      <Settings className="w-4 h-4" />
                      Admin Panel
                    </Link>
                  )}
                  <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="px-2 py-2 text-left flex items-center gap-2" style={{ color: '#5A6B63' }}>
                    <LogOut className="w-4 h-4" /> Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/faq" className="px-2 py-2" style={{ color: '#5A6B63' }} onClick={() => setMobileMenuOpen(false)}>FAQ</Link>
                  <Link to="/login" className="px-2 py-2" style={{ color: '#5A6B63' }} onClick={() => setMobileMenuOpen(false)}>Sign In</Link>
                  <Link to="/register" className="btn-primary text-center mt-2" onClick={() => setMobileMenuOpen(false)}>Sign Up</Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
    <PricingModal show={showPricingModal} onClose={() => setShowPricingModal(false)} />
    </>
  );
};

// Provider Directory Page
const ProviderDirectory = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [providers, setProviders] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const prefilter = location.state || {};
  const [filters, setFilters] = useState({
    category: prefilter.category || "",
    city: "",
    province: prefilter.province || "",
    search: ""
  });
  const [filterOptions, setFilterOptions] = useState({ cities: [], categories: [], provinces: [] });
  const [expandedId, setExpandedId] = useState(null);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [isLimited, setIsLimited] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const userTier = user?.tier || "free";

  useEffect(() => {
    fetchProviders();
    if (user) fetchSaved();
  }, [filters.category, filters.city, filters.province]); // eslint-disable-line react-hooks/exhaustive-deps -- fetchProviders/fetchSaved are stable callbacks that depend on filter state already listed

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append("category", filters.category);
      if (filters.city) params.append("city", filters.city);
      if (filters.province) params.append("province", filters.province);
      if (filters.search) params.append("search", filters.search);
      params.append("tier", userTier);
      const res = await axios.get(`${API}/providers?${params}`);
      setProviders(res.data.providers);
      setFilterOptions(res.data.filter_options);
      setIsLimited(res.data.is_limited);
      setTotalCount(res.data.total);
    } catch (e) {
      console.error("Error fetching providers:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchSaved = async () => {
    try {
      const res = await axios.get(`${API}/providers/saved/list`);
      setSavedIds(new Set(res.data.saved.map(s => s.provider_id)));
    } catch (e) { console.warn("Could not fetch saved providers:", e.message); }
  };

  const toggleSave = async (providerId) => {
    if (!user) return;
    setSavingId(providerId);
    try {
      if (savedIds.has(providerId)) {
        await axios.delete(`${API}/providers/save/${providerId}`);
        setSavedIds(prev => { const n = new Set(prev); n.delete(providerId); return n; });
      } else {
        await axios.post(`${API}/providers/save`, { provider_id: providerId });
        setSavedIds(prev => new Set(prev).add(providerId));
      }
    } catch (e) {
      console.error("Save error:", e);
    } finally {
      setSavingId(null);
    }
  };

  const handleExportCSV = async () => {
    try {
      const res = await axios.get(`${API}/providers/export/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'caremap_saved_providers.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Export error:", e);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProviders();
  };

  const provinceNames = { ON: "Ontario", MB: "Manitoba", BC: "British Columbia", AB: "Alberta", SK: "Saskatchewan", NS: "Nova Scotia", NB: "New Brunswick", PE: "Prince Edward Island", NL: "Newfoundland & Labrador", YT: "Yukon", NT: "Northwest Territories", NU: "Nunavut" };

  const getCategoryColor = (cat) => {
    switch(cat) {
      case 'Home Care': return { bg: '#E8F5E9', text: '#2E7D32', border: '#A5D6A7' };
      case 'Retirement Living': return { bg: '#E3F2FD', text: '#1565C0', border: '#90CAF9' };
      case 'Long-Term Care': return { bg: '#FFF3E0', text: '#E65100', border: '#FFCC80' };
      default: return { bg: '#E6EDE8', text: '#1E3F30', border: '#D5DFD9' };
    }
  };

  const getScoreColor = (score) => {
    if (score >= 85) return { bg: '#1E3F30', text: '#FFFFFF', ring: '#34694D' };
    if (score >= 70) return { bg: '#2D5A44', text: '#FFFFFF', ring: '#4A8B67' };
    if (score >= 55) return { bg: '#5A6B63', text: '#FFFFFF', ring: '#7A8B83' };
    return { bg: '#9CA3AF', text: '#FFFFFF', ring: '#B0B7BF' };
  };

  const getScoreTierStyle = (label) => {
    switch(label) {
      case 'CareMap Premier': return { bg: '#1E3F30', text: '#FFFFFF' };
      case 'CareMap Verified': return { bg: '#34694D', text: '#FFFFFF' };
      case 'CareMap Standard': return { bg: '#5A6B63', text: '#FFFFFF' };
      default: return { bg: '#9CA3AF', text: '#FFFFFF' };
    }
  };

  const [showScoringInfo, setShowScoringInfo] = useState(false);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
        {/* Header */}
        <div className="mb-8">
          {user && (
            <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm mb-4 hover:underline" style={{ color: '#5A6B63' }} data-testid="providers-back-dashboard">
              <ChevronLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
          )}
          <h1 className="font-heading text-3xl sm:text-4xl mb-2" style={{ color: '#232F2A' }} data-testid="provider-directory-title">
            Care Provider Directory
          </h1>
          <p className="text-base" style={{ color: '#5A6B63' }}>
            {filters.province ? (provinceNames[filters.province] || filters.province) : "All regions"} — {totalCount || providers.length} verified agencies
          </p>
        </div>

        {/* Filters & Search Bar */}
        <div className="card mb-8" style={{ backgroundColor: '#F5F0EB' }}>
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#5A6B63' }} />
              <input
                type="text"
                placeholder="Search by name..."
                value={filters.search}
                onChange={e => setFilters(f => ({...f, search: e.target.value}))}
                className="input-base pl-10 w-full"
                data-testid="provider-search"
              />
            </div>
            <select
              value={filters.province}
              onChange={e => setFilters(f => ({...f, province: e.target.value, city: ""}))}
              className="input-base"
              data-testid="province-filter"
            >
              <option value="">All Provinces</option>
              {filterOptions.provinces.map(p => (
                <option key={p} value={p}>{provinceNames[p] || p}</option>
              ))}
            </select>
            <select
              value={filters.category}
              onChange={e => setFilters(f => ({...f, category: e.target.value}))}
              className="input-base"
              data-testid="category-filter"
            >
              <option value="">All Categories</option>
              {filterOptions.categories.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <select
              value={filters.city}
              onChange={e => setFilters(f => ({...f, city: e.target.value}))}
              className="input-base"
              data-testid="city-filter"
            >
              <option value="">All Cities</option>
              {filterOptions.cities.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <button type="submit" className="btn-primary py-2 px-6" data-testid="search-btn">
              Search
            </button>
          </form>
        </div>

        {/* Saved providers bar */}
        {user && savedIds.size > 0 && (
          <div className="flex items-center justify-between mb-6 p-3 rounded-xl" style={{ backgroundColor: '#E6EDE8' }}>
            <p className="text-sm font-medium" style={{ color: '#1E3F30' }}>
              <Heart className="w-4 h-4 inline mr-1" fill="#1E3F30" /> {savedIds.size} provider{savedIds.size > 1 ? 's' : ''} saved
            </p>
            <button onClick={handleExportCSV} className="text-sm flex items-center gap-1 hover:opacity-70" style={{ color: '#1E3F30' }} data-testid="export-csv-btn">
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        )}

        {/* How Scoring Works Info Tile */}
        <div className="mb-6">
          <button
            onClick={() => setShowScoringInfo(!showScoringInfo)}
            className="flex items-center gap-2 text-sm font-medium bg-transparent border-0 cursor-pointer hover:opacity-70"
            style={{ color: '#1E3F30' }}
            data-testid="scoring-info-toggle"
          >
            <HelpCircle className="w-4 h-4" />
            How is the CareMap Score calculated?
            <ChevronRight className={`w-4 h-4 transition-transform ${showScoringInfo ? 'rotate-90' : ''}`} />
          </button>
          {showScoringInfo && (
            <div className="mt-3 p-6 rounded-2xl border" style={{ backgroundColor: '#F8F6F2', borderColor: '#E6EDE8' }} data-testid="scoring-info-panel">
              <h3 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>The CareMap Scoring System</h3>
              <p className="text-sm mb-4" style={{ color: '#5A6B63' }}>
                Every provider receives a CareMap Score from 0–100 based on five weighted categories.
                Scores are adjusted by province to reflect regional care realities.
              </p>
              <div className="grid sm:grid-cols-5 gap-3 mb-5">
                {[
                  { label: 'Care Quality', weight: '30%', icon: Shield, desc: 'Licensing, accreditation, specialized care capabilities' },
                  { label: 'Reputation', weight: '20%', icon: Star, desc: 'Google ratings and review volume' },
                  { label: 'Access', weight: '15%', icon: Clock, desc: 'Waitlists, availability, location' },
                  { label: 'Cost Clarity', weight: '15%', icon: DollarSign, desc: 'Pricing transparency and fee structures' },
                  { label: 'Experience', weight: '20%', icon: Heart, desc: 'Resident experience and environment' }
                ].map(cat => (
                  <div key={cat.label} className="text-center p-3 rounded-xl" style={{ backgroundColor: '#FFFFFF' }}>
                    <cat.icon className="w-5 h-5 mx-auto mb-1" style={{ color: '#1E3F30' }} />
                    <p className="text-sm font-medium" style={{ color: '#232F2A' }}>{cat.label}</p>
                    <p className="text-lg font-heading" style={{ color: '#1E3F30' }}>{cat.weight}</p>
                    <p className="text-xs mt-1" style={{ color: '#5A6B63' }}>{cat.desc}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3 mb-4">
                {[
                  { label: 'Premier', range: '85-100', color: '#1E3F30' },
                  { label: 'Verified', range: '70-84', color: '#34694D' },
                  { label: 'Standard', range: '55-69', color: '#5A6B63' },
                  { label: 'Watch', range: '<55', color: '#9CA3AF' }
                ].map(tier => (
                  <div key={tier.label} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tier.color }} />
                    <span className="text-xs" style={{ color: '#232F2A' }}>
                      <span className="font-medium">{tier.label}</span> ({tier.range})
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-xs" style={{ color: '#9CA3AF' }}>
                Scores are based on publicly available data. They are designed to guide — not replace — professional advice.
                Province-specific adjustments account for regional care system differences.
              </p>
            </div>
          )}
        </div>

        {/* Provider List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 rounded-full animate-spin mx-auto" style={{ borderColor: '#E6EDE8', borderTopColor: '#1E3F30' }} />
          </div>
        ) : providers.length === 0 ? (
          <div className="text-center py-12 card">
            <Search className="w-10 h-10 mx-auto mb-3" style={{ color: '#5A6B63' }} />
            <p className="font-heading text-xl mb-2" style={{ color: '#232F2A' }}>No providers found</p>
            <p style={{ color: '#5A6B63' }}>Try adjusting your filters</p>
          </div>
        ) : (
          <div className="space-y-4">
            {providers.map(provider => {
              const catColor = getCategoryColor(provider.category);
              const isExpanded = expandedId === provider.id;
              const isSaved = savedIds.has(provider.id);

              return (
                <div
                  key={provider.id}
                  className="card transition-all duration-200 hover:shadow-md"
                  data-testid={`provider-${provider.id}`}
                >
                  {/* Main Row */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    {/* Left: Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="font-heading text-lg" style={{ color: '#232F2A' }}>
                          {provider.business_name}
                        </h3>
                        <span
                          className="text-xs px-2 py-0.5 rounded-full border"
                          style={{ backgroundColor: catColor.bg, color: catColor.text, borderColor: catColor.border }}
                        >
                          {provider.category}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" style={{ color: '#5A6B63' }}>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {provider.city}
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3.5 h-3.5" />
                          ${provider.pricing_low}–${provider.pricing_high}/{provider.pricing_unit}
                        </span>
                      </div>
                    </div>

                    {/* Right: CareMap Score + Actions */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {/* CareMap Score */}
                      {provider.caremap_score != null && (
                        <div className="text-center" data-testid={`score-${provider.id}`}>
                          <div
                            className="w-12 h-12 rounded-full flex items-center justify-center font-heading text-lg"
                            style={{ backgroundColor: getScoreColor(provider.caremap_score).bg, color: getScoreColor(provider.caremap_score).text }}
                          >
                            {provider.caremap_score}
                          </div>
                          <p className="text-xs mt-1 font-medium" style={{ color: getScoreTierStyle(provider.score_label).bg }}>
                            {provider.score_label?.replace('CareMap ', '')}
                          </p>
                        </div>
                      )}

                      {/* Google Rating */}
                      {provider.google_rating > 0 && (
                        <div className="text-center px-2 py-1.5 rounded-lg border" style={{ borderColor: '#D5DFD9', backgroundColor: '#F8F6F2' }}>
                          <div className="flex items-center gap-1">
                            <Star className="w-3.5 h-3.5" style={{ color: '#EAB308' }} fill="#EAB308" />
                            <span className="text-sm font-medium" style={{ color: '#232F2A' }}>{provider.google_rating}</span>
                          </div>
                          <p className="text-xs" style={{ color: '#5A6B63' }}>{provider.review_count} reviews</p>
                        </div>
                      )}

                      {/* Save button */}
                      {user && (
                        <button
                          onClick={() => toggleSave(provider.id)}
                          disabled={savingId === provider.id}
                          className="p-2 rounded-full transition-colors hover:bg-gray-100"
                          data-testid={`save-${provider.id}`}
                          title={isSaved ? "Remove from saved" : "Save provider"}
                        >
                          <Heart
                            className="w-5 h-5"
                            style={{ color: isSaved ? '#C26D5C' : '#5A6B63' }}
                            fill={isSaved ? '#C26D5C' : 'none'}
                          />
                        </button>
                      )}

                      {/* Expand */}
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : provider.id)}
                        className="p-2 rounded-full transition-colors hover:bg-gray-100"
                        data-testid={`expand-${provider.id}`}
                      >
                        <ChevronRight
                          className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          style={{ color: '#5A6B63' }}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t" style={{ borderColor: '#E6EDE8' }}>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs font-medium uppercase mb-1" style={{ color: '#5A6B63' }}>Address</p>
                            <p className="text-sm" style={{ color: '#232F2A' }}>{provider.address}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium uppercase mb-1" style={{ color: '#5A6B63' }}>Phone</p>
                            <p className="text-sm" style={{ color: '#232F2A' }}>
                              <a href={`tel:${provider.phone}`} className="hover:underline">{provider.phone}</a>
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-medium uppercase mb-1" style={{ color: '#5A6B63' }}>Website</p>
                            <a
                              href={provider.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm hover:underline"
                              style={{ color: '#1E3F30' }}
                            >
                              {provider.website?.replace('https://', '').replace('http://', '')}
                            </a>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs font-medium uppercase mb-1" style={{ color: '#5A6B63' }}>About</p>
                            <p className="text-sm" style={{ color: '#232F2A' }}>{provider.notes}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium uppercase mb-1" style={{ color: '#5A6B63' }}>Estimated Pricing</p>
                            <p className="text-sm" style={{ color: '#232F2A' }}>
                              <span className="font-medium">${provider.pricing_low} – ${provider.pricing_high}</span>
                              <span style={{ color: '#5A6B63' }}> / {provider.pricing_unit}</span>
                            </p>
                            <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>
                              * Baseline estimate. Confirm directly with provider.
                            </p>
                          </div>
                          {/* Score Breakdown */}
                          {provider.score_breakdown && (
                            <div>
                              <p className="text-xs font-medium uppercase mb-2" style={{ color: '#5A6B63' }}>CareMap Score Breakdown</p>
                              <div className="space-y-1.5">
                                {[
                                  { key: 'care_quality', label: 'Care Quality', weight: 30 },
                                  { key: 'reputation', label: 'Reputation', weight: 20 },
                                  { key: 'access', label: 'Access', weight: 15 },
                                  { key: 'cost_transparency', label: 'Cost Transparency', weight: 15 },
                                  { key: 'experience', label: 'Experience', weight: 20 }
                                ].map(cat => {
                                  const data = provider.score_breakdown[cat.key];
                                  const pct = data ? Math.round((data.raw / 100) * 100) : 0;
                                  return (
                                    <div key={cat.key} className="flex items-center gap-2">
                                      <span className="text-xs w-28 flex-shrink-0" style={{ color: '#5A6B63' }}>{cat.label} ({cat.weight}%)</span>
                                      <div className="flex-1 h-2 rounded-full" style={{ backgroundColor: '#E6EDE8' }}>
                                        <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: pct >= 70 ? '#1E3F30' : pct >= 50 ? '#5A6B63' : '#9CA3AF' }} />
                                      </div>
                                      <span className="text-xs w-8 text-right font-medium" style={{ color: '#232F2A' }}>{data?.score || 0}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-4">
                        <a
                          href={`tel:${provider.phone}`}
                          className="btn-primary text-sm py-2 px-4 inline-flex items-center gap-1"
                        >
                          <Phone className="w-4 h-4" /> Call
                        </a>
                        <a
                          href={provider.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-secondary text-sm py-2 px-4 inline-flex items-center gap-1"
                        >
                          Visit Website
                        </a>
                        {user && (
                          <button
                            onClick={() => toggleSave(provider.id)}
                            className="btn-secondary text-sm py-2 px-4 inline-flex items-center gap-1"
                          >
                            <Heart className="w-4 h-4" fill={isSaved ? '#C26D5C' : 'none'} style={{ color: isSaved ? '#C26D5C' : 'currentColor' }} />
                            {isSaved ? 'Saved' : 'Save'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Upgrade prompt for free tier */}
        {isLimited && (
          <div className="mt-6 p-6 rounded-2xl text-center" style={{ 
            backgroundColor: '#1E3F30', 
            background: 'linear-gradient(135deg, #1E3F30 0%, #2D5A44 100%)'
          }} data-testid="provider-upgrade-box">
            <Lock className="w-8 h-8 mx-auto mb-3" style={{ color: '#D5DFD9' }} />
            <h3 className="font-heading text-2xl mb-2" style={{ color: '#FFFFFF' }}>
              You're seeing 3 of {totalCount} providers
            </h3>
            <p className="mb-5 text-sm max-w-md mx-auto" style={{ color: '#B8C9BE' }}>
              Unlock the full directory to compare all care providers in your area — with detailed pricing, contact info, and the ability to save your shortlist.
            </p>
            <button 
              onClick={() => setShowPricingModal(true)} 
              className="py-3 px-8 rounded-full font-medium transition-all hover:opacity-90"
              style={{ backgroundColor: '#FFFFFF', color: '#1E3F30' }}
              data-testid="provider-upgrade-btn"
            >
              Upgrade to Pro — $29
            </button>
          </div>
        )}

        {/* Login prompt for non-users */}
        {!user && (
          <div className="mt-8 card text-center" style={{ backgroundColor: '#E6EDE8' }}>
            <Users className="w-8 h-8 mx-auto mb-3" style={{ color: '#1E3F30' }} />
            <h3 className="font-heading text-xl mb-2" style={{ color: '#232F2A' }}>
              Save & Export Your Selections
            </h3>
            <p className="mb-4 text-sm" style={{ color: '#5A6B63' }}>
              Sign in to save providers to your account and export your shortlist
            </p>
            <Link to="/register" className="btn-primary inline-block text-sm py-2 px-6" data-testid="provider-signup-btn">
              Create Free Account
            </Link>
          </div>
        )}
      </div>
      <PricingModal show={showPricingModal} onClose={() => setShowPricingModal(false)} />
    </div>
  );
};

// Comparison Tool Page
const ComparisonTool = () => {
  const { user } = useAuth();
  const [selectedTypes, setSelectedTypes] = useState(["home_care", "assisted_living"]);
  const [province, setProvince] = useState("ON");
  const [comparisons, setComparisons] = useState([]);
  const [loading, setLoading] = useState(false);
  const userTier = user?.tier || "free";
  const [showPricingModal, setShowPricingModal] = useState(false);
  
  const toggleType = (type) => {
    if (selectedTypes.includes(type)) {
      setSelectedTypes(selectedTypes.filter(t => t !== type));
    } else if (selectedTypes.length < 4) {
      setSelectedTypes([...selectedTypes, type]);
    }
  };
  
  const runComparison = async () => {
    if (selectedTypes.length < 2) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/compare?tier=${userTier}`, {
        care_types: selectedTypes,
        province
      });
      setComparisons(res.data.comparisons);
    } catch (e) {
      if (e.response?.status === 403) {
        alert("Comparison tool requires Pro subscription");
      }
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (userTier !== "free" && selectedTypes.length >= 2) {
      runComparison();
    }
  }, [selectedTypes, province, userTier]);
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      <Navigation />
      <SocialProof />
      
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl mb-4" style={{ color: '#232F2A' }}>
            <Scale className="w-10 h-10 inline-block mr-3" style={{ color: '#1E3F30' }} />
            Compare Care Options
          </h1>
          <p style={{ color: '#5A6B63' }}>
            See side-by-side comparisons of different care types
          </p>
        </div>
        
        {userTier === "free" ? (
          <div className="card text-center max-w-lg mx-auto">
            <Lock className="w-12 h-12 mx-auto mb-4" style={{ color: '#1E3F30' }} />
            <h2 className="font-heading text-2xl mb-3" style={{ color: '#232F2A' }}>
              Pro Feature
            </h2>
            <p className="mb-6" style={{ color: '#5A6B63' }}>
              The comparison tool helps you evaluate care options side-by-side with costs, pros, cons, and wait times.
            </p>
            <button onClick={() => setShowPricingModal(true)} className="btn-primary inline-block" data-testid="compare-upgrade-btn">
              Upgrade to Pro - $29
            </button>
          </div>
        ) : (
          <>
            {/* Selection Controls */}
            <div className="card mb-8">
              <div className="flex flex-wrap items-center gap-4 mb-6">
                <span className="font-medium" style={{ color: '#232F2A' }}>Province:</span>
                <select 
                  value={province}
                  onChange={e => setProvince(e.target.value)}
                  className="input-base max-w-xs"
                >
                  <option value="ON">Ontario</option>
                  <option value="BC">British Columbia</option>
                  <option value="AB">Alberta</option>
                </select>
              </div>
              
              <div className="flex flex-wrap gap-3">
                <span className="font-medium" style={{ color: '#232F2A' }}>Compare:</span>
                {["home_care", "assisted_living", "long_term_care", "memory_care"].map(type => (
                  <button
                    key={type}
                    onClick={() => toggleType(type)}
                    className={`px-4 py-2 rounded-full text-sm transition-colors ${
                      selectedTypes.includes(type) 
                        ? 'text-white' 
                        : ''
                    }`}
                    style={{ 
                      backgroundColor: selectedTypes.includes(type) ? '#1E3F30' : '#E6EDE8',
                      color: selectedTypes.includes(type) ? 'white' : '#232F2A'
                    }}
                    data-testid={`compare-${type}`}
                  >
                    {formatCareType(type)}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Comparison Grid */}
            {loading ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 rounded-full animate-spin mx-auto" style={{ borderColor: '#E6EDE8', borderTopColor: '#1E3F30' }} />
              </div>
            ) : comparisons.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {comparisons.map(comp => (
                  <div key={comp.type} className="card" data-testid={`comparison-${comp.type}`}>
                    <h3 className="font-heading text-xl mb-4 text-center" style={{ color: '#232F2A' }}>
                      {comp.name}
                    </h3>
                    
                    <div className="text-center mb-4 p-4 rounded-xl" style={{ backgroundColor: '#E6EDE8' }}>
                      <p className="text-sm" style={{ color: '#5A6B63' }}>Monthly Cost</p>
                      <p className="font-heading text-2xl" style={{ color: '#1E3F30' }}>
                        ${comp.monthly_cost_low.toLocaleString()} - ${comp.monthly_cost_high.toLocaleString()}
                      </p>
                    </div>
                    
                    <div className="mb-4">
                      <p className="text-sm font-medium mb-2" style={{ color: '#34694D' }}>Pros</p>
                      <ul className="space-y-1">
                        {comp.pros.map((pro) => (
                          <li key={pro} className="text-sm flex items-start gap-2" style={{ color: '#5A6B63' }}>
                            <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#34694D' }} />
                            {pro}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="mb-4">
                      <p className="text-sm font-medium mb-2" style={{ color: '#B91C1C' }}>Cons</p>
                      <ul className="space-y-1">
                        {comp.cons.map((con) => (
                          <li key={con} className="text-sm flex items-start gap-2" style={{ color: '#5A6B63' }}>
                            <X className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#B91C1C' }} />
                            {con}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="pt-4 border-t" style={{ borderColor: '#D5DFD9' }}>
                      <p className="text-sm" style={{ color: '#5A6B63' }}>
                        <strong>Best for:</strong> {comp.best_for}
                      </p>
                      <p className="text-sm mt-2" style={{ color: '#5A6B63' }}>
                        <strong>Wait time:</strong> {comp.typical_wait}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center" style={{ color: '#5A6B63' }}>
                Select at least 2 care types to compare
              </p>
            )}
          </>
        )}
      </div>
      <PricingModal show={showPricingModal} onClose={() => setShowPricingModal(false)} />
    </div>
  );
};

// Community Page
const CommunityPage = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [showNewPost, setShowNewPost] = useState(false);
  const [newPost, setNewPost] = useState({ title: "", content: "", category: "question" });
  const userTier = user?.tier || "free";
  const [showPricingModal, setShowPricingModal] = useState(false);
  
  useEffect(() => {
    fetchPosts();
  }, [category]);
  
  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = category ? `?category=${category}` : "";
      const res = await axios.get(`${API}/community/posts${params}`);
      setPosts(res.data.posts);
    } catch (e) {
      console.error("Error fetching posts:", e);
    } finally {
      setLoading(false);
    }
  };
  
  const handleLike = async (postId) => {
    try {
      await axios.post(`${API}/community/posts/${postId}/like`);
      setPosts(posts.map(p => p.id === postId ? {...p, likes: p.likes + 1} : p));
    } catch (e) {
      console.error("Error liking post:", e);
    }
  };
  
  // Sample posts for demo
  const samplePosts = [
    { id: "sample_1", title: "How do I know when it's time for memory care?", content: "My mother has moderate dementia and I'm struggling to know when home care isn't enough anymore...", category: "question", author_name: "CaringDaughter", likes: 24, comment_count: 12, created_at: "2026-03-30" },
    { id: "sample_2", title: "Our journey finding the right facility", content: "After 6 months of searching, we finally found a wonderful assisted living facility for my dad. Here's what we learned...", category: "story", author_name: "OntarioSon", likes: 47, comment_count: 8, created_at: "2026-03-28" },
    { id: "sample_3", title: "Tips for managing caregiver burnout", content: "I've been caring for my parents for 3 years now. Here are some strategies that have helped me cope...", category: "resource", author_name: "BurnoutSurvivor", likes: 89, comment_count: 23, created_at: "2026-03-25" },
  ];
  
  const displayPosts = posts.length > 0 ? posts : samplePosts;
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      <Navigation />
      <SocialProof />
      
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl mb-4" style={{ color: '#232F2A' }}>
            <Users className="w-10 h-10 inline-block mr-3" style={{ color: '#1E3F30' }} />
            Caregiver Community
          </h1>
          <p style={{ color: '#5A6B63' }}>
            Connect with others on the same journey
          </p>
        </div>
        
        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {[
            { value: "", label: "All Posts" },
            { value: "question", label: "Questions" },
            { value: "story", label: "Stories" },
            { value: "resource", label: "Resources" },
            { value: "support", label: "Support" }
          ].map(cat => (
            <button
              key={cat.value}
              onClick={() => setCategory(cat.value)}
              className={`px-4 py-2 rounded-full text-sm transition-colors`}
              style={{ 
                backgroundColor: category === cat.value ? '#1E3F30' : '#E6EDE8',
                color: category === cat.value ? 'white' : '#232F2A'
              }}
            >
              {cat.label}
            </button>
          ))}
          
          <button
            onClick={() => userTier === "free" ? alert("Posting requires Pro subscription") : setShowNewPost(true)}
            className="ml-auto btn-primary text-sm py-2 px-4 flex items-center gap-2"
            data-testid="new-post-btn"
          >
            {userTier === "free" && <Lock className="w-4 h-4" />}
            New Post
          </button>
        </div>
        
        {/* Posts */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 rounded-full animate-spin mx-auto" style={{ borderColor: '#E6EDE8', borderTopColor: '#1E3F30' }} />
          </div>
        ) : (
          <div className="space-y-6">
            {displayPosts.map(post => (
              <div key={post.id} className="card" data-testid={`post-${post.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <span className="text-xs px-2 py-1 rounded-full mr-2" style={{ backgroundColor: '#E6EDE8', color: '#1E3F30' }}>
                      {formatCareType(post.category)}
                    </span>
                    <span className="text-sm" style={{ color: '#5A6B63' }}>
                      by {post.author_name}
                    </span>
                  </div>
                  <span className="text-sm" style={{ color: '#5A6B63' }}>
                    {post.created_at}
                  </span>
                </div>
                
                <h3 className="font-heading text-xl mb-2" style={{ color: '#232F2A' }}>
                  {post.title}
                </h3>
                <p className="text-sm mb-4" style={{ color: '#5A6B63' }}>
                  {post.content.slice(0, 200)}...
                </p>
                
                <div className="flex items-center gap-6">
                  <button 
                    onClick={() => handleLike(post.id)}
                    className="flex items-center gap-2 text-sm hover:opacity-70 transition-opacity"
                    style={{ color: '#5A6B63' }}
                  >
                    <ThumbsUp className="w-4 h-4" />
                    {post.likes}
                  </button>
                  <span className="flex items-center gap-2 text-sm" style={{ color: '#5A6B63' }}>
                    <MessageCircle className="w-4 h-4" />
                    {post.comment_count} comments
                    {userTier === "free" && <Lock className="w-3 h-3" />}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {userTier === "free" && (
          <div className="mt-8 card text-center" style={{ backgroundColor: '#E6EDE8' }}>
            <MessageCircle className="w-8 h-8 mx-auto mb-3" style={{ color: '#1E3F30' }} />
            <h3 className="font-heading text-xl mb-2" style={{ color: '#232F2A' }}>
              Join the Conversation
            </h3>
            <p className="mb-4" style={{ color: '#5A6B63' }}>
              Upgrade to Pro to post questions and comment on others' experiences
            </p>
            <button onClick={() => setShowPricingModal(true)} className="btn-primary inline-block" data-testid="community-upgrade-btn">
              Upgrade to Pro - $29
            </button>
          </div>
        )}
      </div>
      <PricingModal show={showPricingModal} onClose={() => setShowPricingModal(false)} />
    </div>
  );
};

// Care Coordinator Marketplace
const CoordinatorMarketplace = () => {
  const { user } = useAuth();
  const [coordinators, setCoordinators] = useState([]);
  const [loading, setLoading] = useState(true);
  const userTier = user?.tier || "free";
  const [selectedCoordinator, setSelectedCoordinator] = useState(null);
  const [message, setMessage] = useState("");
  const [showPricingModal, setShowPricingModal] = useState(false);
  
  useEffect(() => {
    fetchCoordinators();
  }, []);
  
  const fetchCoordinators = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/coordinators?tier=${userTier}`);
      setCoordinators(res.data.coordinators);
    } catch (e) {
      console.error("Error fetching coordinators:", e);
    } finally {
      setLoading(false);
    }
  };
  
  // Full coordinator list for demo
  const fullCoordinators = [
    { id: "coord_1", name: "Sarah Mitchell", title: "Geriatric Care Manager", province: "ON", city: "Toronto", rating: 4.9, reviews: 47, hourly_rate: 125, specialties: ["dementia", "family_mediation", "care_planning"], bio: "15+ years helping families navigate complex care decisions.", available: true, response_time: "Within 24 hours" },
    { id: "coord_2", name: "Dr. James Chen", title: "Senior Care Consultant", province: "ON", city: "Ottawa", rating: 4.8, reviews: 62, hourly_rate: 150, specialties: ["medical_advocacy", "long_term_planning", "crisis_management"], bio: "Former hospital administrator specializing in elder care transitions.", available: true, response_time: "Within 48 hours" },
    { id: "coord_3", name: "Marie Tremblay", title: "Care Navigator", province: "BC", city: "Vancouver", rating: 4.7, reviews: 38, hourly_rate: 110, specialties: ["home_care", "subsidies", "waitlist_navigation"], bio: "Bilingual coordinator helping families access provincial resources.", available: true, response_time: "Within 24 hours" },
    { id: "coord_4", name: "Robert Williams", title: "Elder Care Advocate", province: "AB", city: "Calgary", rating: 4.9, reviews: 55, hourly_rate: 135, specialties: ["legal_planning", "financial_guidance", "facility_selection"], bio: "Social worker with deep knowledge of Alberta's care system.", available: false, response_time: "1 week" },
  ];
  
  const displayCoordinators = userTier === "premium" ? fullCoordinators : fullCoordinators.slice(0, 2).map(c => ({...c, locked: true}));
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      <Navigation />
      <SocialProof />
      
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl mb-4" style={{ color: '#232F2A' }}>
            <UserCheck className="w-10 h-10 inline-block mr-3" style={{ color: '#1E3F30' }} />
            Care Coordinator Marketplace
          </h1>
          <p style={{ color: '#5A6B63' }}>
            Connect with professional care coordinators who can guide your journey
          </p>
        </div>
        
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 rounded-full animate-spin mx-auto" style={{ borderColor: '#E6EDE8', borderTopColor: '#1E3F30' }} />
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {displayCoordinators.map(coord => (
                <div key={coord.id} className="card relative" data-testid={`coordinator-${coord.id}`}>
                  {coord.locked && (
                    <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-2xl flex items-center justify-center z-10">
                      <div className="text-center p-6">
                        <Lock className="w-8 h-8 mx-auto mb-3" style={{ color: '#1E3F30' }} />
                        <p className="font-medium" style={{ color: '#232F2A' }}>Premium Only</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#E6EDE8' }}>
                      <UserCheck className="w-8 h-8" style={{ color: '#1E3F30' }} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-heading text-xl" style={{ color: '#232F2A' }}>{coord.name}</h3>
                          <p className="text-sm" style={{ color: '#5A6B63' }}>{coord.title}</p>
                          <p className="text-sm" style={{ color: '#5A6B63' }}>{coord.city}, {coord.province}</p>
                        </div>
                        {coord.available ? (
                          <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#34694D', color: 'white' }}>
                            Available
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#5A6B63', color: 'white' }}>
                            Busy
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 mt-2">
                        <Star className="w-4 h-4" style={{ color: '#EAB308' }} fill="#EAB308" />
                        <span className="font-medium" style={{ color: '#232F2A' }}>{coord.rating}</span>
                        <span className="text-sm" style={{ color: '#5A6B63' }}>({coord.reviews} reviews)</span>
                      </div>
                      
                      <p className="text-sm mt-3" style={{ color: '#5A6B63' }}>{coord.bio}</p>
                      
                      <div className="flex flex-wrap gap-2 mt-3">
                        {coord.specialties.map(s => (
                          <span key={s} className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#E6EDE8', color: '#1E3F30' }}>
                            {formatCareType(s)}
                          </span>
                        ))}
                      </div>
                      
                      <div className="flex items-center justify-between mt-4 pt-4 border-t" style={{ borderColor: '#D5DFD9' }}>
                        <div>
                          <p className="font-heading text-xl" style={{ color: '#1E3F30' }}>${coord.hourly_rate}/hr</p>
                          <p className="text-xs" style={{ color: '#5A6B63' }}>Response: {coord.response_time}</p>
                        </div>
                        <button 
                          onClick={() => userTier === "premium" ? setSelectedCoordinator(coord) : null}
                          className="btn-primary text-sm py-2 px-4 flex items-center gap-2"
                          disabled={coord.locked}
                        >
                          <Send className="w-4 h-4" />
                          Contact
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {userTier !== "premium" && (
              <div className="card text-center" style={{ backgroundColor: '#1E3F30' }}>
                <Award className="w-12 h-12 mx-auto mb-4 text-white" />
                <h2 className="font-heading text-2xl mb-3 text-white">
                  Unlock Care Coordinator Access
                </h2>
                <p className="mb-6 text-white/80 max-w-xl mx-auto">
                  Premium members get full access to our vetted network of care coordinators who can help you 
                  navigate the system, find the right facilities, and advocate for your loved one.
                </p>
                <button onClick={() => setShowPricingModal(true)} className="btn-accent inline-block" data-testid="coordinator-upgrade-btn">
                  Upgrade to Premium - $99
                </button>
              </div>
            )}
          </>
        )}
        
        {/* Contact Modal */}
        {selectedCoordinator && (
          <div className="modal-overlay" onClick={() => setSelectedCoordinator(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <button onClick={() => setSelectedCoordinator(null)} className="absolute top-4 right-4">
                <X className="w-6 h-6" style={{ color: '#5A6B63' }} />
              </button>
              <h3 className="font-heading text-2xl mb-4" style={{ color: '#232F2A' }}>
                Contact {selectedCoordinator.name}
              </h3>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Describe your situation and what help you need..."
                className="input-base h-32 resize-none mb-4"
              />
              <button className="btn-primary w-full">
                Send Message
              </button>
            </div>
          </div>
        )}
      </div>
      <PricingModal show={showPricingModal} onClose={() => setShowPricingModal(false)} />
    </div>
  );
};

// Payment Success Page
const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const pollStatus = async (attempts = 0) => {
      if (attempts >= 5) {
        setStatus("timeout");
        return;
      }

      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("success");
        } else if (res.data.status === "expired") {
          setStatus("failed");
        } else {
          setTimeout(() => pollStatus(attempts + 1), 2000);
        }
      } catch (e) {
        console.error("Status check error:", e);
        setStatus("error");
      }
    };

    if (sessionId) {
      pollStatus();
    }
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps -- API and axios are stable module-level constants

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FDFBF7' }}>
      <div className="card max-w-md w-full text-center mx-4">
        {status === "loading" && (
          <>
            <div className="w-16 h-16 border-4 rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#E6EDE8', borderTopColor: '#1E3F30' }} />
            <h2 className="font-heading text-2xl mb-2" style={{ color: '#232F2A' }}>Processing Payment</h2>
            <p style={{ color: '#5A6B63' }}>Please wait while we confirm your payment...</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4" style={{ backgroundColor: '#34694D' }}>
              <Check className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-heading text-2xl mb-2" style={{ color: '#232F2A' }}>Payment Successful!</h2>
            <p className="mb-6" style={{ color: '#5A6B63' }}>Thank you for upgrading. You now have access to your full report.</p>
            <button onClick={() => navigate("/assessment")} className="btn-primary" data-testid="back-to-report-btn">
              View Your Full Report
            </button>
          </>
        )}
        {(status === "failed" || status === "error" || status === "timeout") && (
          <>
            <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4" style={{ backgroundColor: '#B91C1C' }}>
              <X className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-heading text-2xl mb-2" style={{ color: '#232F2A' }}>Payment Issue</h2>
            <p className="mb-6" style={{ color: '#5A6B63' }}>There was an issue processing your payment. Please try again.</p>
            <button onClick={() => navigate("/assessment")} className="btn-primary" data-testid="try-again-btn">
              Return to Assessment
            </button>
          </>
        )}
      </div>
      <PricingModal show={showPricingModal} onClose={() => setShowPricingModal(false)} />
    </div>
  );
};

// Auth Context
const AuthContext = React.createContext(null);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    checkAuth();
  }, []);
  
  const checkAuth = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch (e) {
      // Access token may have expired — try refreshing
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
      } catch {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(res.data);
    return res.data;
  };
  
  const register = async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    setUser(res.data);
    return res.data;
  };
  
  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
    window.location.href = "/signed-out";
  };
  
  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => React.useContext(AuthContext);

// Login Page
const LoginPage = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FDFBF7' }}>
      <div className="card max-w-md w-full mx-4">
        <div className="text-center mb-8">
          <Link to="/" className="font-heading text-2xl" style={{ color: '#1E3F30' }}>CareMap Canada</Link>
          <h1 className="font-heading text-3xl mt-4" style={{ color: '#232F2A' }}>Welcome Back</h1>
          <p style={{ color: '#5A6B63' }}>Sign in to your account</p>
        </div>
        
        {error && (
          <div className="mb-4 p-3 rounded-xl text-sm" style={{ backgroundColor: '#FEE2E2', color: '#B91C1C' }}>
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#232F2A' }}>Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="input-base"
              placeholder="you@example.com"
              required
              data-testid="login-email"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#232F2A' }}>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="input-base"
              placeholder="••••••••"
              required
              data-testid="login-password"
            />
          </div>
          <div className="text-right">
            <Link to="/forgot-password" className="text-sm hover:underline" style={{ color: '#1E3F30' }}>
              Forgot password?
            </Link>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full flex items-center justify-center gap-2"
            data-testid="login-submit"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
        
        <p className="text-center mt-6" style={{ color: '#5A6B63' }}>
          Don't have an account?{" "}
          <Link to="/register" className="font-medium hover:underline" style={{ color: '#1E3F30' }}>
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
};

// Register Page
const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (user && user.email_verified !== false) navigate("/dashboard");
  }, [user, navigate]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    
    setLoading(true);
    try {
      const data = await register(email, password, name);
      if (data.email_verified === false) {
        navigate("/verify-email");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center py-12" style={{ backgroundColor: '#FDFBF7' }}>
      <div className="card max-w-md w-full mx-4">
        <div className="text-center mb-8">
          <Link to="/" className="font-heading text-2xl" style={{ color: '#1E3F30' }}>CareMap Canada</Link>
          <h1 className="font-heading text-3xl mt-4" style={{ color: '#232F2A' }}>Create Account</h1>
          <p style={{ color: '#5A6B63' }}>Start your care journey today</p>
        </div>
        
        {error && (
          <div className="mb-4 p-3 rounded-xl text-sm" style={{ backgroundColor: '#FEE2E2', color: '#B91C1C' }}>
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#232F2A' }}>Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="input-base"
              placeholder="Your name"
              data-testid="register-name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#232F2A' }}>Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="input-base"
              placeholder="you@example.com"
              required
              data-testid="register-email"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#232F2A' }}>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="input-base"
              placeholder="••••••••"
              required
              data-testid="register-password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#232F2A' }}>Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              className="input-base"
              placeholder="••••••••"
              required
              data-testid="register-confirm"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full"
            data-testid="register-submit"
          >
            {loading ? "Creating account..." : "Create Account"}
          </button>
        </form>
        
        <p className="text-center mt-6" style={{ color: '#5A6B63' }}>
          Already have an account?{" "}
          <Link to="/login" className="font-medium hover:underline" style={{ color: '#1E3F30' }}>
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

// Verify Email Page
const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    if (user.email_verified) { navigate("/dashboard"); return; }
    inputRefs[0].current?.focus();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (resendCooldown > 0) {
      const t = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendCooldown]);

  const handleChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 5) inputRefs[index + 1].current?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(""));
      inputRefs[5].current?.focus();
    }
  };

  const handleVerify = async () => {
    const fullCode = code.join("");
    if (fullCode.length !== 6) { setError("Please enter the full 6-digit code"); return; }
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-email`, { email: user.email, code: fullCode });
      await checkAuth();
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      await axios.post(`${API}/auth/resend-verification`, {}, { withCredentials: true });
      setResendCooldown(60);
      setError("");
    } catch {
      setError("Failed to resend code");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12" style={{ backgroundColor: '#FDFBF7' }}>
      <div className="card max-w-md w-full mx-4 text-center">
        <Link to="/" className="font-heading text-2xl" style={{ color: '#1E3F30' }}>CareMap Canada</Link>
        <div className="w-16 h-16 mx-auto mt-6 mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E6EDE8' }}>
          <Mail className="w-7 h-7" style={{ color: '#1E3F30' }} />
        </div>
        <h1 className="font-heading text-2xl mb-2" style={{ color: '#232F2A' }} data-testid="verify-email-title">Verify Your Email</h1>
        <p className="text-sm mb-6" style={{ color: '#5A6B63' }}>
          We sent a 6-digit code to <strong style={{ color: '#232F2A' }}>{user?.email}</strong>
        </p>

        {error && (
          <div className="mb-4 p-3 rounded-xl text-sm" style={{ backgroundColor: '#FEE2E2', color: '#B91C1C' }} data-testid="verify-error">
            {error}
          </div>
        )}

        <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste} data-testid="otp-inputs">
          {code.map((digit, i) => (
            <input
              key={i}
              ref={inputRefs[i]}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              className="w-12 h-14 text-center text-2xl font-bold rounded-xl border-2 focus:outline-none transition-colors"
              style={{
                borderColor: digit ? '#1E3F30' : '#D5DFD9',
                color: '#1E3F30',
                backgroundColor: '#FDFBF7'
              }}
              data-testid={`otp-input-${i}`}
            />
          ))}
        </div>

        <button
          onClick={handleVerify}
          disabled={loading || code.join("").length !== 6}
          className="btn-primary w-full mb-4"
          data-testid="verify-submit"
        >
          {loading ? "Verifying..." : "Verify Email"}
        </button>

        <p className="text-sm" style={{ color: '#5A6B63' }}>
          Didn't receive the code?{" "}
          <button
            onClick={handleResend}
            disabled={resendCooldown > 0}
            className="font-medium bg-transparent border-0 cursor-pointer hover:underline"
            style={{ color: resendCooldown > 0 ? '#5A6B63' : '#1E3F30' }}
            data-testid="resend-code-btn"
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend Code"}
          </button>
        </p>
      </div>
    </div>
  );
};

// Forgot Password Page
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
    } catch (err) {
      // Still show success to prevent email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FDFBF7' }}>
      <div className="card max-w-md w-full mx-4">
        <div className="text-center mb-8">
          <Link to="/" className="font-heading text-2xl" style={{ color: '#1E3F30' }}>CareMap Canada</Link>
          <h1 className="font-heading text-3xl mt-4" style={{ color: '#232F2A' }}>Reset Password</h1>
        </div>
        
        {submitted ? (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4" style={{ backgroundColor: '#E6EDE8' }}>
              <Mail className="w-8 h-8" style={{ color: '#1E3F30' }} />
            </div>
            <p className="mb-4" style={{ color: '#5A6B63' }}>
              If an account exists for {email}, you'll receive a password reset link shortly.
            </p>
            <Link to="/login" className="btn-primary inline-block">
              Back to Login
            </Link>
          </div>
        ) : (
          <>
            <p className="mb-6 text-center" style={{ color: '#5A6B63' }}>
              Enter your email and we'll send you a link to reset your password.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#232F2A' }}>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="input-base"
                  placeholder="you@example.com"
                  required
                  data-testid="forgot-email"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full"
                data-testid="forgot-submit"
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </button>
            </form>
            <p className="text-center mt-6" style={{ color: '#5A6B63' }}>
              <Link to="/login" className="hover:underline" style={{ color: '#1E3F30' }}>
                Back to Login
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

// Reset Password Page
const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };
  
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FDFBF7' }}>
        <div className="card max-w-md w-full mx-4 text-center">
          <h1 className="font-heading text-2xl mb-4" style={{ color: '#232F2A' }}>Invalid Link</h1>
          <p style={{ color: '#5A6B63' }}>This password reset link is invalid or has expired.</p>
          <Link to="/forgot-password" className="btn-primary inline-block mt-4">
            Request New Link
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FDFBF7' }}>
      <div className="card max-w-md w-full mx-4">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl" style={{ color: '#232F2A' }}>Set New Password</h1>
        </div>
        
        {success ? (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4" style={{ backgroundColor: '#34694D' }}>
              <Check className="w-8 h-8 text-white" />
            </div>
            <p style={{ color: '#5A6B63' }}>Password reset successful! Redirecting to login...</p>
          </div>
        ) : (
          <>
            {error && (
              <div className="mb-4 p-3 rounded-xl text-sm" style={{ backgroundColor: '#FEE2E2', color: '#B91C1C' }}>
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#232F2A' }}>New Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="input-base"
                  placeholder="••••••••"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#232F2A' }}>Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="input-base"
                  placeholder="••••••••"
                  required
                />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full">
                {loading ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

// User Dashboard Page
const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [savedAssessments, setSavedAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationResent, setVerificationResent] = useState(false);
  
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    } else if (user) {
      fetchSavedAssessments();
    }
  }, [user, authLoading, navigate]);
  
  const fetchSavedAssessments = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/assessment/saved/${user.email}`);
      setSavedAssessments(res.data.assessments || []);
    } catch (e) {
      console.error("Error fetching assessments:", e);
    } finally {
      setLoading(false);
    }
  };
  
  const deleteAssessment = async (id) => {
    if (!window.confirm("Are you sure you want to delete this assessment?")) return;
    try {
      await axios.delete(`${API}/assessment/saved/${id}`);
      setSavedAssessments(savedAssessments.filter(a => a.id !== id));
    } catch (e) {
      console.error("Error deleting assessment:", e);
    }
  };
  
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FDFBF7' }}>
        <div className="w-12 h-12 border-4 rounded-full animate-spin" style={{ borderColor: '#E6EDE8', borderTopColor: '#1E3F30' }} />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      <Navigation />
      
      {/* Email Verification Banner */}
      {user?.email_verified === false && (
        <div className="border-b" style={{ backgroundColor: '#FFF7ED', borderColor: '#FDBA74' }} data-testid="verify-email-banner">
          <div className="max-w-5xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm" style={{ color: '#9A3412' }}>
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>Verify your email to unlock all features.</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={async () => {
                  setResendingVerification(true);
                  try {
                    await axios.post(`${API}/auth/resend-verification`, {}, { withCredentials: true });
                    setVerificationResent(true);
                    setTimeout(() => setVerificationResent(false), 5000);
                  } catch { /* ignore */ }
                  setResendingVerification(false);
                }}
                disabled={resendingVerification || verificationResent}
                className="text-sm font-medium bg-transparent border-0 cursor-pointer underline"
                style={{ color: '#9A3412' }}
                data-testid="banner-resend-btn"
              >
                {verificationResent ? "Code sent!" : resendingVerification ? "Sending..." : "Resend code"}
              </button>
              <Link to="/verify-email" className="text-sm font-medium px-3 py-1.5 rounded-full" style={{ backgroundColor: '#1E3F30', color: 'white' }} data-testid="banner-verify-link">
                Enter Code
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* User Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl" style={{ color: '#232F2A' }}>
              Welcome back, {user?.name || 'there'}
            </h1>
            <p style={{ color: '#5A6B63' }}>Manage your care journey</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm px-3 py-1 rounded-full capitalize" style={{ 
              backgroundColor: user?.tier === 'premium' ? '#C26D5C' : user?.tier === 'pro' ? '#1E3F30' : '#E6EDE8',
              color: user?.tier === 'free' ? '#232F2A' : 'white'
            }}>
              {user?.tier || 'free'} plan
            </span>
            {user?.tier === 'free' && (
              <button onClick={() => setShowPricingModal(true)} className="btn-primary text-sm py-2 px-4" data-testid="dashboard-upgrade-btn">
                Upgrade
              </button>
            )}
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className={`grid ${user?.role === 'admin' ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} gap-4 mb-12`}>
          <Link to="/assessment" className="card flex items-center gap-4 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E6EDE8' }}>
              <Activity className="w-6 h-6" style={{ color: '#1E3F30' }} />
            </div>
            <div>
              <h3 className="font-medium" style={{ color: '#232F2A' }}>New Assessment</h3>
              <p className="text-sm" style={{ color: '#5A6B63' }}>Start a new CareMap</p>
            </div>
          </Link>
          <Link to="/providers" className="card flex items-center gap-4 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E6EDE8' }}>
              <Search className="w-6 h-6" style={{ color: '#1E3F30' }} />
            </div>
            <div>
              <h3 className="font-medium" style={{ color: '#232F2A' }}>Find Providers</h3>
              <p className="text-sm" style={{ color: '#5A6B63' }}>Browse care options</p>
            </div>
          </Link>
          <Link to="/community" className="card flex items-center gap-4 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E6EDE8' }}>
              <Users className="w-6 h-6" style={{ color: '#1E3F30' }} />
            </div>
            <div>
              <h3 className="font-medium" style={{ color: '#232F2A' }}>Community</h3>
              <p className="text-sm" style={{ color: '#5A6B63' }}>Connect with others</p>
            </div>
          </Link>
          {user?.role === "admin" && (
            <Link to="/admin" className="card flex items-center gap-4 hover:shadow-lg transition-shadow" style={{ borderLeft: '3px solid #1E3F30' }} data-testid="dashboard-admin-link">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1E3F30' }}>
                <Settings className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-medium" style={{ color: '#232F2A' }}>Admin Panel</h3>
                <p className="text-sm" style={{ color: '#5A6B63' }}>Manage users & data</p>
              </div>
            </Link>
          )}
        </div>
        
        {/* Saved Assessments */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl" style={{ color: '#232F2A' }}>Saved Assessments</h2>
            {user?.tier === 'free' && savedAssessments.length >= 1 && (
              <span className="text-sm" style={{ color: '#5A6B63' }}>
                <Lock className="w-4 h-4 inline mr-1" />
                Upgrade to Pro to save more
              </span>
            )}
          </div>
          
          {loading ? (
            <div className="card text-center py-12">
              <div className="w-10 h-10 border-4 rounded-full animate-spin mx-auto" style={{ borderColor: '#E6EDE8', borderTopColor: '#1E3F30' }} />
            </div>
          ) : savedAssessments.length === 0 ? (
            <div className="card text-center py-12">
              <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4" style={{ backgroundColor: '#E6EDE8' }}>
                <BookOpen className="w-8 h-8" style={{ color: '#1E3F30' }} />
              </div>
              <h3 className="font-heading text-xl mb-2" style={{ color: '#232F2A' }}>No saved assessments yet</h3>
              <p className="mb-6" style={{ color: '#5A6B63' }}>Complete an assessment to save your CareMap report</p>
              <Link to="/assessment" className="btn-primary inline-block">
                Start Your First Assessment
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {savedAssessments.map(assessment => (
                <div key={assessment.id} className="card flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#E6EDE8' }}>
                      <MapPin className="w-6 h-6" style={{ color: '#1E3F30' }} />
                    </div>
                    <div>
                      <h3 className="font-medium" style={{ color: '#232F2A' }}>{assessment.name}</h3>
                      <p className="text-sm" style={{ color: '#5A6B63' }}>
                        Province: {assessment.assessment_data?.parent_profile?.province || assessment.assessment_data?.province || 'N/A'}
                      </p>
                      <p className="text-xs" style={{ color: '#5A6B63' }}>
                        Saved: {new Date(assessment.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    {assessment.report_id && (
                      <button 
                        onClick={async () => {
                          try {
                            const res = await axios.get(`${API}/report/${assessment.report_id}`);
                            navigate("/report", { state: { report: res.data, formData: assessment.assessment_data } });
                          } catch (e) {
                            console.error("Error loading report:", e);
                            alert("Could not load this report. It may no longer be available.");
                          }
                        }}
                        className="text-sm px-4 py-2 rounded-full font-medium transition-colors"
                        style={{ backgroundColor: '#1E3F30', color: 'white' }}
                        data-testid={`view-report-${assessment.id}`}
                      >
                        View Report
                      </button>
                    )}
                    <button 
                      onClick={() => deleteAssessment(assessment.id)}
                      className="text-sm px-3 py-2 rounded-full hover:bg-red-50 transition-colors"
                      style={{ color: '#B91C1C' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Tier Benefits */}
        <div className="card" style={{ backgroundColor: '#1E3F30' }}>
          <h3 className="font-heading text-xl mb-4 text-white">Your {user?.tier || 'Free'} Plan Includes:</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {user?.tier === 'free' ? (
              <>
                <div className="flex items-center gap-2 text-white/80">
                  <Check className="w-4 h-4" style={{ color: '#34694D' }} />
                  <span>1 saved assessment</span>
                </div>
                <div className="flex items-center gap-2 text-white/80">
                  <Check className="w-4 h-4" style={{ color: '#34694D' }} />
                  <span>3 provider listings</span>
                </div>
                <div className="flex items-center gap-2 text-white/80">
                  <Check className="w-4 h-4" style={{ color: '#34694D' }} />
                  <span>Community (read-only)</span>
                </div>
                <div className="flex items-center gap-2 text-white/80">
                  <Check className="w-4 h-4" style={{ color: '#34694D' }} />
                  <span>Basic cost overview</span>
                </div>
              </>
            ) : user?.tier === 'pro' ? (
              <>
                <div className="flex items-center gap-2 text-white/80">
                  <Check className="w-4 h-4" style={{ color: '#34694D' }} />
                  <span>Unlimited saved assessments</span>
                </div>
                <div className="flex items-center gap-2 text-white/80">
                  <Check className="w-4 h-4" style={{ color: '#34694D' }} />
                  <span>Full provider directory</span>
                </div>
                <div className="flex items-center gap-2 text-white/80">
                  <Check className="w-4 h-4" style={{ color: '#34694D' }} />
                  <span>Comparison tool</span>
                </div>
                <div className="flex items-center gap-2 text-white/80">
                  <Check className="w-4 h-4" style={{ color: '#34694D' }} />
                  <span>Community participation</span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 text-white/80">
                  <Check className="w-4 h-4" style={{ color: '#34694D' }} />
                  <span>Everything in Pro</span>
                </div>
                <div className="flex items-center gap-2 text-white/80">
                  <Check className="w-4 h-4" style={{ color: '#34694D' }} />
                  <span>Care coordinator marketplace</span>
                </div>
                <div className="flex items-center gap-2 text-white/80">
                  <Check className="w-4 h-4" style={{ color: '#34694D' }} />
                  <span>Direct messaging</span>
                </div>
                <div className="flex items-center gap-2 text-white/80">
                  <Check className="w-4 h-4" style={{ color: '#34694D' }} />
                  <span>Priority support</span>
                </div>
              </>
            )}
          </div>
          {user?.tier !== 'premium' && (
            <div className="mt-6 pt-6 border-t border-white/20">
              <button onClick={() => setShowPricingModal(true)} className="btn-accent inline-flex items-center gap-2" data-testid="dashboard-upgrade-pro-btn">
                <Star className="w-4 h-4" />
                Upgrade to {user?.tier === 'free' ? 'Pro' : 'Premium'}
              </button>
            </div>
          )}
        </div>
      </div>
      <PricingModal show={showPricingModal} onClose={() => setShowPricingModal(false)} />
    </div>
  );
};

// FAQ Page
const FaqPage = () => {
  const navigate = useNavigate();
  const faqs = [
    {
      q: "How does CareMap Canada help me?",
      a: "CareMap Canada walks you through a simple 5-step assessment about your parent's health, daily abilities, finances, and urgency. In minutes, our AI generates a personalized CareMap report with recommended care paths, estimated costs by province, and next steps — so you can stop guessing and start planning."
    },
    {
      q: "What is the CareMap Score and how is it calculated?",
      a: "Every provider in our directory receives a CareMap Score from 0 to 100 based on five weighted categories: Care Quality (30%), Reputation (20%), Access & Availability (15%), Cost Transparency (15%), and Experience (20%). Scores are adjusted by province to reflect regional care realities. Providers scoring 85+ earn CareMap Premier status."
    },
    {
      q: "How much does elder care actually cost in Canada?",
      a: "Costs vary significantly by province and care type. Home care typically ranges from $25–$65/hour. Retirement living runs $2,500–$8,000/month depending on the province and level of service. Long-term care co-pays are regulated and generally range from $1,900–$2,700/month. Your personalized CareMap report includes province-specific cost breakdowns."
    },
    {
      q: "What's the difference between Free, Pro, and Premium?",
      a: "The Free tier gives you a basic CareMap report and access to 3 provider listings. Pro ($29 CAD) unlocks the full provider directory across all provinces, detailed cost analysis, the ability to save providers and export your shortlist, and the comparison tool. Premium ($99 CAD) adds access to the Care Coordinator Marketplace and priority support."
    },
    {
      q: "Is my parent's health information kept private?",
      a: "Absolutely. We comply with Canada's PIPEDA privacy legislation. Your assessment data is used solely to generate your care recommendations — we never sell it to third parties. Payment is processed securely through Stripe, and we don't store credit card numbers on our servers. You can request deletion of your data at any time."
    },
    {
      q: "What provinces does CareMap Canada cover?",
      a: "We currently serve all of Canada except Quebec (due to its distinct healthcare framework). Our provider directory has verified agencies across Ontario, British Columbia, and Manitoba, with more provinces being added regularly. Our AI-generated reports cover province-specific programs and subsidies for all supported provinces."
    },
    {
      q: "How do I know when it's time to look into care for my parent?",
      a: "Common signs include difficulty with daily activities (bathing, cooking, medication management), frequent falls, increased confusion or memory issues, social isolation, or caregiver burnout in the family. If you're asking this question, it's likely time. Our assessment takes just 5 minutes and gives you clarity on where things stand."
    },
    {
      q: "Can I save my assessment and come back to it later?",
      a: "Yes — with a free account you can save one assessment, and Pro or Premium members can save unlimited assessments. This is helpful if you're exploring options for multiple family members or want to revisit your plan as circumstances change."
    },
    {
      q: "What if there aren't many providers listed in my area?",
      a: "We're actively expanding our directory province by province. If your area has limited listings, our system automatically shows the nearest available providers. Your CareMap report also includes province-wide programs and government resources that may be available regardless of your location."
    },
    {
      q: "Do I need to be in Canada to use CareMap Canada?",
      a: "No. Many of our users are adult children living abroad who are coordinating care for a parent in Canada. As long as the person receiving care is in a supported Canadian province, you can complete the assessment and access the directory from anywhere in the world."
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      <nav className="py-4 px-4 sm:px-8 border-b" style={{ borderColor: '#D5DFD9' }}>
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="font-heading text-xl" style={{ color: '#1E3F30' }}>CareMap Canada</Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm hover:opacity-70" style={{ color: '#5A6B63' }}>Home</Link>
            <button onClick={() => navigate("/assessment")} className="btn-primary text-sm py-2 px-5">Get Your CareMap</button>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 py-12 sm:py-16">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl sm:text-5xl mb-3" style={{ color: '#232F2A' }} data-testid="faq-heading">
            Frequently Asked Questions
          </h1>
          <p className="text-base" style={{ color: '#5A6B63' }}>
            We know this is overwhelming. Here are the questions we hear most from families like yours.
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq) => (
            <FaqItem key={faq.q} question={faq.q} answer={faq.a} />
          ))}
        </div>

        <div className="mt-12 text-center p-8 rounded-2xl" style={{ backgroundColor: '#E6EDE8' }}>
          <h3 className="font-heading text-xl mb-2" style={{ color: '#232F2A' }}>Still have questions?</h3>
          <p className="text-sm mb-4" style={{ color: '#5A6B63' }}>
            Our team is here to help you navigate your parent's care journey.
          </p>
          <a href="mailto:support@caremapcanada.com" className="btn-primary inline-block text-sm py-2 px-6">
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};

// Terms of Service Page
const TermsPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      <nav className="py-4 px-4 sm:px-8 border-b" style={{ borderColor: '#D5DFD9' }}>
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="font-heading text-xl" style={{ color: '#1E3F30' }}>CareMap Canada™</Link>
          <Link to="/" className="text-sm hover:underline" style={{ color: '#5A6B63' }}>Back to Home</Link>
        </div>
      </nav>
      <div className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="font-heading text-4xl mb-2" style={{ color: '#232F2A' }} data-testid="terms-heading">Terms of Service</h1>
        <p className="text-sm mb-8" style={{ color: '#5A6B63' }}>Last updated: March 31, 2026</p>

        <div className="space-y-8" style={{ color: '#3A4A42' }}>
          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>1. Acceptance of Terms</h2>
            <p className="leading-relaxed">By accessing or using CareMap Canada™ ("the Service"), operated by CareMap Canada ("we," "us," or "our"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>2. Description of Service</h2>
            <p className="leading-relaxed">CareMap Canada provides an online platform that helps Canadians (excluding Quebec) navigate elder care decisions through personalized assessments, care provider directories, and AI-generated guidance reports. The Service is available in Free, Pro ($29 CAD), and Premium ($99 CAD) tiers.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>3. Not Medical or Legal Advice</h2>
            <p className="leading-relaxed">The information provided through CareMap Canada is for general informational purposes only. It does not constitute medical, legal, financial, or professional advice. Always consult qualified healthcare professionals, legal advisors, or financial planners before making care decisions for your loved ones.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>4. User Accounts</h2>
            <p className="leading-relaxed">You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must provide accurate and complete information when creating an account and promptly update any changes. You must be at least 18 years of age to use this Service.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>5. Payments and Refunds</h2>
            <p className="leading-relaxed">Paid tiers (Pro and Premium) are one-time purchases processed securely through Stripe. All prices are in Canadian Dollars (CAD). Due to the immediate delivery of digital content, refunds are handled on a case-by-case basis. Please contact us at support@caremapcanada.com within 14 days of purchase if you are unsatisfied with the Service.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>6. User Content</h2>
            <p className="leading-relaxed">By posting content in community forums or other areas of the Service, you grant CareMap Canada a non-exclusive, royalty-free license to use, display, and distribute that content within the platform. You are solely responsible for any content you post and must not share content that is unlawful, abusive, defamatory, or violates the rights of others.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>7. Intellectual Property</h2>
            <p className="leading-relaxed">All content, features, and functionality of the Service — including text, graphics, logos, and software — are the property of CareMap Canada and are protected by Canadian and international intellectual property laws. You may not reproduce, distribute, or create derivative works without our prior written consent.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>8. Limitation of Liability</h2>
            <p className="leading-relaxed">To the maximum extent permitted by law, CareMap Canada shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service. Our total liability shall not exceed the amount you paid for the Service in the twelve months preceding the claim.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>9. Modifications</h2>
            <p className="leading-relaxed">We reserve the right to modify these Terms at any time. Material changes will be communicated via email or a notice on the Service. Your continued use of the Service after such changes constitutes acceptance of the updated Terms.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>10. Governing Law</h2>
            <p className="leading-relaxed">These Terms are governed by and construed in accordance with the laws of the Province of Ontario and the federal laws of Canada applicable therein. Any disputes shall be resolved in the courts of Ontario, Canada.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>11. Contact Us</h2>
            <p className="leading-relaxed">If you have any questions about these Terms, please contact us at <a href="mailto:support@caremapcanada.com" className="underline" style={{ color: '#1E3F30' }}>support@caremapcanada.com</a>.</p>
          </section>
        </div>
      </div>
    </div>
  );
};

// Privacy Policy Page
const PrivacyPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      <nav className="py-4 px-4 sm:px-8 border-b" style={{ borderColor: '#D5DFD9' }}>
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="font-heading text-xl" style={{ color: '#1E3F30' }}>CareMap Canada™</Link>
          <Link to="/" className="text-sm hover:underline" style={{ color: '#5A6B63' }}>Back to Home</Link>
        </div>
      </nav>
      <div className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="font-heading text-4xl mb-2" style={{ color: '#232F2A' }} data-testid="privacy-heading">Privacy Policy</h1>
        <p className="text-sm mb-8" style={{ color: '#5A6B63' }}>Last updated: March 31, 2026</p>

        <div className="space-y-8" style={{ color: '#3A4A42' }}>
          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>1. Introduction</h2>
            <p className="leading-relaxed">CareMap Canada™ ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our website and services, in compliance with Canada's Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable provincial privacy legislation.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>2. Information We Collect</h2>
            <p className="leading-relaxed mb-3">We collect the following types of information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Account Information:</strong> Name, email address, and encrypted password when you create an account.</li>
              <li><strong>Assessment Data:</strong> Information you provide about your parent's health, functional abilities, financial situation, and care preferences during the assessment process.</li>
              <li><strong>Payment Information:</strong> Payment details are processed securely by Stripe. We do not store your credit card numbers on our servers.</li>
              <li><strong>Usage Data:</strong> Information about how you interact with the Service, including pages visited and features used.</li>
              <li><strong>Community Content:</strong> Posts, comments, and other content you share in community forums.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>3. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>To generate personalized CareMap reports and care recommendations.</li>
              <li>To maintain and improve the Service.</li>
              <li>To process payments and manage your account tier.</li>
              <li>To send important service-related communications.</li>
              <li>To connect you with care providers and coordinators (with your consent).</li>
              <li>To ensure the security and integrity of the Service.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>4. Health Information</h2>
            <p className="leading-relaxed">We understand the sensitivity of health-related information provided during assessments. This data is used solely to generate your personalized care recommendations. We do not sell health information to third parties. Assessment data is stored securely and can be deleted at your request.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>5. AI-Generated Content</h2>
            <p className="leading-relaxed">Our Service uses artificial intelligence to generate personalized care reports. Your assessment data is processed by our AI systems to create recommendations. This data is not used to train AI models and is handled in accordance with this Privacy Policy.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>6. Data Sharing and Disclosure</h2>
            <p className="leading-relaxed mb-3">We do not sell your personal information. We may share your information with:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Service Providers:</strong> Trusted third parties who assist in operating the Service (e.g., Stripe for payments, SendGrid for email).</li>
              <li><strong>Care Providers/Coordinators:</strong> Only when you explicitly request to be connected with them.</li>
              <li><strong>Legal Requirements:</strong> When required by law, regulation, or legal process.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>7. Data Security</h2>
            <p className="leading-relaxed">We implement industry-standard security measures including encrypted data transmission (SSL/TLS), secure password hashing (bcrypt), and access controls to protect your personal information. However, no method of transmission over the internet is 100% secure.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>8. Data Retention</h2>
            <p className="leading-relaxed">We retain your personal information for as long as your account is active or as needed to provide the Service. You may request deletion of your account and associated data at any time by contacting us. We will respond to deletion requests within 30 days.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>9. Your Rights</h2>
            <p className="leading-relaxed mb-3">Under Canadian privacy law, you have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access the personal information we hold about you.</li>
              <li>Request correction of inaccurate information.</li>
              <li>Request deletion of your personal information.</li>
              <li>Withdraw consent for the use of your information.</li>
              <li>File a complaint with the Office of the Privacy Commissioner of Canada.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>10. Cookies</h2>
            <p className="leading-relaxed">We use essential cookies to maintain your session and authentication status. These cookies are necessary for the Service to function and cannot be disabled. We do not use third-party tracking or advertising cookies.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>11. Children's Privacy</h2>
            <p className="leading-relaxed">The Service is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>12. Changes to This Policy</h2>
            <p className="leading-relaxed">We may update this Privacy Policy from time to time. We will notify you of material changes by email or by posting a prominent notice on the Service. Your continued use of the Service after changes take effect constitutes acceptance of the updated policy.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl mb-3" style={{ color: '#232F2A' }}>13. Contact Us</h2>
            <p className="leading-relaxed">If you have questions or concerns about this Privacy Policy or our data practices, please contact our Privacy Officer at <a href="mailto:privacy@caremapcanada.com" className="underline" style={{ color: '#1E3F30' }}>privacy@caremapcanada.com</a>.</p>
          </section>
        </div>
      </div>
    </div>
  );
};

// ============ ARTICLES / BLOG ============
const ArticlesPage = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticles();
  }, [activeCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = activeCategory ? `?category=${encodeURIComponent(activeCategory)}` : "";
      const res = await axios.get(`${API}/articles${params}`);
      setArticles(res.data.articles);
      setCategories(res.data.categories);
    } catch (e) {
      console.error("Error fetching articles:", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
        <div className="text-center mb-10">
          <h1 className="font-heading text-3xl sm:text-4xl mb-3" style={{ color: '#232F2A' }} data-testid="articles-title">
            Caregiving Insights & Resources
          </h1>
          <p className="text-base max-w-2xl mx-auto" style={{ color: '#5A6B63' }}>
            Expert guidance, practical tips, and compassionate advice for Canadian families navigating elder care
          </p>
        </div>

        {/* Category Filters */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center mb-8" data-testid="article-categories">
            <button
              onClick={() => setActiveCategory("")}
              className="text-sm px-4 py-2 rounded-full transition-colors"
              style={{
                backgroundColor: !activeCategory ? '#1E3F30' : '#E6EDE8',
                color: !activeCategory ? 'white' : '#232F2A'
              }}
            >
              All Articles
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className="text-sm px-4 py-2 rounded-full transition-colors"
                style={{
                  backgroundColor: activeCategory === cat ? '#1E3F30' : '#E6EDE8',
                  color: activeCategory === cat ? 'white' : '#232F2A'
                }}
                data-testid={`cat-${cat.toLowerCase().replace(/\s/g, '-')}`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-10 h-10 border-4 rounded-full animate-spin" style={{ borderColor: '#E6EDE8', borderTopColor: '#1E3F30' }} />
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="w-12 h-12 mx-auto mb-4" style={{ color: '#D5DFD9' }} />
            <p className="text-lg" style={{ color: '#5A6B63' }}>No articles yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-grid">
            {articles.map(article => (
              <Link
                key={article.id}
                to={`/articles/${article.slug}`}
                className="card overflow-hidden hover:shadow-lg transition-shadow group p-0"
                data-testid={`article-card-${article.slug}`}
              >
                {article.cover_image && (
                  <div className="h-48 overflow-hidden">
                    <img
                      src={article.cover_image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-5">
                  <span className="text-xs font-medium px-2 py-1 rounded-full mb-3 inline-block" style={{ backgroundColor: '#E6EDE8', color: '#1E3F30' }}>
                    {article.category}
                  </span>
                  <h3 className="font-heading text-lg mb-2 group-hover:underline" style={{ color: '#232F2A' }}>
                    {article.title}
                  </h3>
                  <p className="text-sm mb-3 line-clamp-3" style={{ color: '#5A6B63' }}>
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: '#5A6B63' }}>
                      {article.created_at ? new Date(article.created_at).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' }) : ""}
                    </span>
                    <span className="text-xs font-medium flex items-center gap-1" style={{ color: '#1E3F30' }}>
                      Read <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ArticlePageWrapper = () => {
  const location = useLocation();
  const slug = location.pathname.split('/articles/')[1] || '';
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      axios.get(`${API}/articles/${slug}`)
        .then(res => setArticle(res.data))
        .catch(e => console.error("Article not found:", e))
        .finally(() => setLoading(false));
    }
  }, [slug]);

  const renderBody = (body) => {
    if (!body) return null;
    return body.split('\n\n').map((paragraph, i) => {
      if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
        return <h2 key={i} className="font-heading text-xl mt-8 mb-3" style={{ color: '#232F2A' }}>{paragraph.replace(/\*\*/g, '')}</h2>;
      }
      if (paragraph.startsWith('*') && paragraph.endsWith('*') && !paragraph.startsWith('**')) {
        return <p key={i} className="text-base italic mb-4" style={{ color: '#5A6B63' }}>{paragraph.replace(/\*/g, '')}</p>;
      }
      // Handle bold text within paragraphs
      const parts = paragraph.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={i} className="text-base mb-4 leading-relaxed" style={{ color: '#232F2A' }}>
          {parts.map((part, j) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={j} style={{ color: '#1E3F30' }}>{part.replace(/\*\*/g, '')}</strong>;
            }
            return <span key={j}>{part}</span>;
          })}
        </p>
      );
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
        <Navigation />
        <div className="flex justify-center py-20">
          <div className="w-10 h-10 border-4 rounded-full animate-spin" style={{ borderColor: '#E6EDE8', borderTopColor: '#1E3F30' }} />
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
        <Navigation />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <h1 className="font-heading text-2xl mb-4" style={{ color: '#232F2A' }}>Article Not Found</h1>
          <Link to="/articles" className="btn-primary text-sm">Back to Articles</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      <Navigation />
      
      {/* Hero */}
      {article.cover_image && (
        <div className="h-64 sm:h-80 relative overflow-hidden">
          <img src={article.cover_image} alt={article.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(transparent 40%, rgba(29,63,48,0.85))' }} />
          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 max-w-3xl mx-auto">
            <span className="text-xs font-medium px-2 py-1 rounded-full mb-3 inline-block" style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}>
              {article.category}
            </span>
            <h1 className="font-heading text-2xl sm:text-3xl text-white" data-testid="article-title">{article.title}</h1>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 py-8">
        {!article.cover_image && (
          <>
            <span className="text-xs font-medium px-2 py-1 rounded-full mb-3 inline-block" style={{ backgroundColor: '#E6EDE8', color: '#1E3F30' }}>
              {article.category}
            </span>
            <h1 className="font-heading text-2xl sm:text-3xl mb-4" style={{ color: '#232F2A' }} data-testid="article-title">{article.title}</h1>
          </>
        )}
        
        <div className="flex items-center gap-4 mb-8 pb-6 border-b" style={{ borderColor: '#E6EDE8' }}>
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1E3F30' }}>
            <User className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-sm font-medium" style={{ color: '#232F2A' }}>{article.author}</p>
            <p className="text-xs" style={{ color: '#5A6B63' }}>
              {article.created_at ? new Date(article.created_at).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' }) : ""}
            </p>
          </div>
        </div>

        <div className="prose" data-testid="article-body">
          {renderBody(article.body)}
        </div>

        <div className="mt-12 pt-8 border-t" style={{ borderColor: '#E6EDE8' }}>
          <div className="card text-center" style={{ backgroundColor: '#1E3F30' }}>
            <h3 className="font-heading text-xl text-white mb-2">Ready to get started?</h3>
            <p className="text-sm text-white/70 mb-4">Get a personalized care plan for your loved one in minutes</p>
            <Link to="/assessment" className="btn-accent inline-flex items-center gap-2">
              Start Your Free Assessment
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/articles" className="text-sm flex items-center gap-1 justify-center hover:underline" style={{ color: '#1E3F30' }}>
            <ChevronLeft className="w-4 h-4" />
            Back to All Articles
          </Link>
        </div>
      </div>
    </div>
  );
};

// ============ SIGNED OUT PAGE ============
const SignedOutPage = () => {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FDFBF7' }}>
      <div className="max-w-md w-full mx-4 text-center">
        <div className="card py-12 px-8">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#E6EDE8' }}>
            <Check className="w-8 h-8" style={{ color: '#1E3F30' }} />
          </div>
          <h1 className="font-heading text-2xl mb-3" style={{ color: '#232F2A' }} data-testid="signedout-title">
            You've been signed out
          </h1>
          <p className="text-sm mb-8" style={{ color: '#5A6B63' }}>
            Thank you for using CareMap Canada. Your session has ended securely.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/login" className="btn-primary text-sm py-2.5 px-6" data-testid="signedout-signin-btn">
              Sign In Again
            </Link>
            <Link to="/" className="btn-secondary text-sm py-2.5 px-6" data-testid="signedout-home-btn">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ ADMIN PANEL ============
const AdminPanel = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersMeta, setUsersMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [assessments, setAssessments] = useState([]);
  const [assessmentsMeta, setAssessmentsMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [editingTier, setEditingTier] = useState(null);
  const [adminArticles, setAdminArticles] = useState([]);
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({ title: "", category: "Caregiving Tips", excerpt: "", body: "", cover_image: "" });

  useEffect(() => {
    // Wait for auth to finish loading before checking role
    if (authLoading) return;
    if (!user || user.role !== "admin") {
      navigate("/dashboard");
      return;
    }
    fetchStats();
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    if (activeTab === "users") fetchUsers();
    if (activeTab === "assessments") fetchAssessments();
    if (activeTab === "articles") fetchAdminArticles();
  }, [activeTab, user]); // eslint-disable-line react-hooks/exhaustive-deps -- tab change or user ready triggers fetch

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/stats`);
      setStats(res.data);
    } catch (e) {
      console.error("Error fetching admin stats:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (page = 1) => {
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: "15" });
      if (searchQuery) params.append("search", searchQuery);
      if (tierFilter) params.append("tier", tierFilter);
      const res = await axios.get(`${API}/admin/users?${params}`);
      setUsers(res.data.users);
      setUsersMeta({ total: res.data.total, page: res.data.page, pages: res.data.pages });
    } catch (e) {
      console.error("Error fetching users:", e);
    }
  };

  const fetchAssessments = async (page = 1) => {
    try {
      const res = await axios.get(`${API}/admin/assessments?page=${page}&limit=15`);
      setAssessments(res.data.assessments);
      setAssessmentsMeta({ total: res.data.total, page: res.data.page, pages: res.data.pages });
    } catch (e) {
      console.error("Error fetching assessments:", e);
    }
  };

  const updateUserTier = async (userId, newTier) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/tier`, { tier: newTier });
      setEditingTier(null);
      fetchUsers(usersMeta.page);
      fetchStats();
    } catch (e) {
      console.error("Error updating tier:", e);
    }
  };

  const exportUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users/export`, { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = 'caremap_users.csv';
      a.click();
    } catch (e) {
      console.error("Export error:", e);
    }
  };

  const fetchAdminArticles = async () => {
    try {
      const res = await axios.get(`${API}/articles?limit=50`);
      setAdminArticles(res.data.articles);
    } catch (e) {
      console.error("Error fetching articles:", e);
    }
  };

  const saveArticle = async () => {
    try {
      if (editingArticle) {
        await axios.put(`${API}/admin/articles/${editingArticle.id}`, articleForm);
      } else {
        await axios.post(`${API}/admin/articles`, articleForm);
      }
      setShowArticleForm(false);
      setEditingArticle(null);
      setArticleForm({ title: "", category: "Caregiving Tips", excerpt: "", body: "", cover_image: "" });
      fetchAdminArticles();
    } catch (e) {
      console.error("Error saving article:", e);
    }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try {
      await axios.delete(`${API}/admin/articles/${id}`);
      fetchAdminArticles();
    } catch (e) {
      console.error("Error deleting article:", e);
    }
  };

  const startEditArticle = async (article) => {
    try {
      const res = await axios.get(`${API}/articles/${article.slug}`);
      setArticleForm({
        title: res.data.title,
        category: res.data.category,
        excerpt: res.data.excerpt,
        body: res.data.body,
        cover_image: res.data.cover_image || ""
      });
      setEditingArticle(res.data);
      setShowArticleForm(true);
    } catch (e) {
      console.error("Error loading article:", e);
    }
  };

  const handleUserSearch = (e) => {
    e.preventDefault();
    fetchUsers(1);
  };

  // Show loading while auth is checking
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FDFBF7' }}>
        <div className="w-10 h-10 border-4 rounded-full animate-spin" style={{ borderColor: '#E6EDE8', borderTopColor: '#1E3F30' }} />
      </div>
    );
  }

  if (!user || user.role !== "admin") return null;

  const tabs = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "users", label: "Users", icon: Users },
    { id: "assessments", label: "Assessments", icon: FileText },
    { id: "articles", label: "Articles", icon: BookOpen },
    { id: "providers", label: "Providers", icon: MapPin },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      <Navigation />

      {/* Admin Header */}
      <div className="py-6" style={{ backgroundColor: '#1E3F30' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
                <Settings className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-2xl text-white" data-testid="admin-panel-title">Admin Panel</h1>
                <p className="text-white/60 text-sm">Manage users, view analytics, and monitor activity</p>
              </div>
            </div>
            <button onClick={fetchStats} className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm" data-testid="admin-refresh-btn">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b" style={{ backgroundColor: '#E6EDE8', borderColor: '#D5DFD9' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap"
                style={{
                  borderColor: activeTab === tab.id ? '#1E3F30' : 'transparent',
                  color: activeTab === tab.id ? '#1E3F30' : '#5A6B63',
                  backgroundColor: 'transparent'
                }}
                data-testid={`admin-tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading && !stats ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 rounded-full animate-spin" style={{ borderColor: '#E6EDE8', borderTopColor: '#1E3F30' }} />
          </div>
        ) : (
          <>
            {/* OVERVIEW TAB */}
            {activeTab === "overview" && stats && (
              <div className="space-y-8 animate-fadeIn" data-testid="admin-overview">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="card" data-testid="stat-total-users">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#E6EDE8' }}>
                        <Users className="w-5 h-5" style={{ color: '#1E3F30' }} />
                      </div>
                      <p className="text-sm" style={{ color: '#5A6B63' }}>Total Users</p>
                    </div>
                    <p className="font-heading text-3xl" style={{ color: '#232F2A' }}>{stats.total_users}</p>
                    <p className="text-xs mt-1" style={{ color: '#34694D' }}>+{stats.recent_registrations} this week</p>
                  </div>
                  <div className="card" data-testid="stat-assessments">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#E6EDE8' }}>
                        <FileText className="w-5 h-5" style={{ color: '#1E3F30' }} />
                      </div>
                      <p className="text-sm" style={{ color: '#5A6B63' }}>Assessments</p>
                    </div>
                    <p className="font-heading text-3xl" style={{ color: '#232F2A' }}>{stats.total_assessments}</p>
                  </div>
                  <div className="card" data-testid="stat-providers">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#E6EDE8' }}>
                        <MapPin className="w-5 h-5" style={{ color: '#1E3F30' }} />
                      </div>
                      <p className="text-sm" style={{ color: '#5A6B63' }}>Providers</p>
                    </div>
                    <p className="font-heading text-3xl" style={{ color: '#232F2A' }}>{stats.total_providers}</p>
                  </div>
                  <div className="card" data-testid="stat-pending-claims">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: stats.pending_claims > 0 ? '#FEF3C7' : '#E6EDE8' }}>
                        <AlertTriangle className="w-5 h-5" style={{ color: stats.pending_claims > 0 ? '#D97706' : '#1E3F30' }} />
                      </div>
                      <p className="text-sm" style={{ color: '#5A6B63' }}>Pending Claims</p>
                    </div>
                    <p className="font-heading text-3xl" style={{ color: '#232F2A' }}>{stats.pending_claims}</p>
                  </div>
                </div>

                {/* Tier Breakdown + Province Stats */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="card" data-testid="tier-breakdown">
                    <h3 className="font-heading text-lg mb-4 flex items-center gap-2" style={{ color: '#232F2A' }}>
                      <Star className="w-5 h-5" style={{ color: '#1E3F30' }} />
                      User Tier Breakdown
                    </h3>
                    <div className="space-y-3">
                      {[
                        { label: "Free", count: stats.tier_breakdown.free, color: '#5A6B63' },
                        { label: "Pro", count: stats.tier_breakdown.pro, color: '#1E3F30' },
                        { label: "Premium", count: stats.tier_breakdown.premium, color: '#C26D5C' }
                      ].map(tier => (
                        <div key={tier.label} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tier.color }} />
                            <span className="text-sm" style={{ color: '#232F2A' }}>{tier.label}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="w-32 h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#E6EDE8' }}>
                              <div className="h-full rounded-full" style={{ 
                                backgroundColor: tier.color, 
                                width: `${stats.total_users > 0 ? (tier.count / stats.total_users * 100) : 0}%` 
                              }} />
                            </div>
                            <span className="text-sm font-medium w-8 text-right" style={{ color: '#232F2A' }}>{tier.count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="card" data-testid="province-stats">
                    <h3 className="font-heading text-lg mb-4 flex items-center gap-2" style={{ color: '#232F2A' }}>
                      <MapPin className="w-5 h-5" style={{ color: '#1E3F30' }} />
                      Assessments by Province
                    </h3>
                    {stats.province_stats.length > 0 ? (
                      <div className="space-y-2">
                        {stats.province_stats.map(ps => (
                          <div key={ps.province} className="flex items-center justify-between py-1.5">
                            <span className="text-sm" style={{ color: '#232F2A' }}>{ps.province}</span>
                            <span className="text-sm font-medium px-3 py-0.5 rounded-full" style={{ backgroundColor: '#E6EDE8', color: '#1E3F30' }}>{ps.count}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm" style={{ color: '#5A6B63' }}>No assessment data yet</p>
                    )}
                  </div>
                </div>

                {/* Care Path Stats */}
                {stats.care_path_stats.length > 0 && (
                  <div className="card" data-testid="care-path-stats">
                    <h3 className="font-heading text-lg mb-4 flex items-center gap-2" style={{ color: '#232F2A' }}>
                      <Activity className="w-5 h-5" style={{ color: '#1E3F30' }} />
                      Recommended Care Paths
                    </h3>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {stats.care_path_stats.map(cp => (
                        <div key={cp.care_path} className="p-4 rounded-xl text-center" style={{ backgroundColor: '#E6EDE8' }}>
                          <p className="font-heading text-2xl mb-1" style={{ color: '#1E3F30' }}>{cp.count}</p>
                          <p className="text-xs" style={{ color: '#5A6B63' }}>{formatCareType(cp.care_path)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* USERS TAB */}
            {activeTab === "users" && (
              <div className="space-y-6 animate-fadeIn" data-testid="admin-users">
                {/* Search & Filters */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <form onSubmit={handleUserSearch} className="flex-1 flex gap-2">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder="Search by email or name..."
                      className="input-base flex-1"
                      data-testid="admin-user-search"
                    />
                    <button type="submit" className="btn-primary text-sm px-4" data-testid="admin-search-btn">
                      <Search className="w-4 h-4" />
                    </button>
                  </form>
                  <select
                    value={tierFilter}
                    onChange={e => { setTierFilter(e.target.value); setTimeout(() => fetchUsers(1), 0); }}
                    className="input-base w-auto"
                    data-testid="admin-tier-filter"
                  >
                    <option value="">All Tiers</option>
                    <option value="free">Free</option>
                    <option value="pro">Pro</option>
                    <option value="premium">Premium</option>
                  </select>
                  <button onClick={exportUsers} className="btn-secondary text-sm flex items-center gap-2 px-4" data-testid="admin-export-btn">
                    <Download className="w-4 h-4" />
                    Export CSV
                  </button>
                </div>

                {/* Users Table */}
                <div className="card overflow-hidden p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr style={{ backgroundColor: '#E6EDE8' }}>
                          <th className="text-left text-xs font-medium uppercase tracking-wider px-4 py-3" style={{ color: '#5A6B63' }}>User</th>
                          <th className="text-left text-xs font-medium uppercase tracking-wider px-4 py-3" style={{ color: '#5A6B63' }}>Role</th>
                          <th className="text-left text-xs font-medium uppercase tracking-wider px-4 py-3" style={{ color: '#5A6B63' }}>Tier</th>
                          <th className="text-left text-xs font-medium uppercase tracking-wider px-4 py-3" style={{ color: '#5A6B63' }}>Registered</th>
                          <th className="text-left text-xs font-medium uppercase tracking-wider px-4 py-3" style={{ color: '#5A6B63' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map(u => (
                          <tr key={u._id} className="border-t" style={{ borderColor: '#E6EDE8' }} data-testid={`user-row-${u._id}`}>
                            <td className="px-4 py-3">
                              <p className="text-sm font-medium" style={{ color: '#232F2A' }}>{u.name || "—"}</p>
                              <p className="text-xs" style={{ color: '#5A6B63' }}>{u.email}</p>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-xs px-2 py-1 rounded-full capitalize" style={{
                                backgroundColor: u.role === "admin" ? '#1E3F30' : '#E6EDE8',
                                color: u.role === "admin" ? 'white' : '#232F2A'
                              }}>{u.role || "user"}</span>
                            </td>
                            <td className="px-4 py-3">
                              {editingTier === u._id ? (
                                <div className="flex items-center gap-1">
                                  {["free", "pro", "premium"].map(t => (
                                    <button
                                      key={t}
                                      onClick={() => updateUserTier(u._id, t)}
                                      className="text-xs px-2 py-1 rounded-full capitalize transition-colors"
                                      style={{
                                        backgroundColor: u.tier === t ? '#1E3F30' : '#E6EDE8',
                                        color: u.tier === t ? 'white' : '#232F2A'
                                      }}
                                      data-testid={`set-tier-${t}`}
                                    >
                                      {t}
                                    </button>
                                  ))}
                                  <button onClick={() => setEditingTier(null)} className="text-xs ml-1" style={{ color: '#5A6B63' }}>
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : (
                                <span className="text-xs px-2 py-1 rounded-full capitalize" style={{
                                  backgroundColor: u.tier === "premium" ? '#C26D5C' : u.tier === "pro" ? '#1E3F30' : '#E6EDE8',
                                  color: u.tier === "free" ? '#232F2A' : 'white'
                                }}>{u.tier || "free"}</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-xs" style={{ color: '#5A6B63' }}>
                                {u.created_at ? new Date(u.created_at).toLocaleDateString('en-CA') : "—"}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {u.role !== "admin" && (
                                <button
                                  onClick={() => setEditingTier(editingTier === u._id ? null : u._id)}
                                  className="text-xs flex items-center gap-1 hover:opacity-70"
                                  style={{ color: '#1E3F30' }}
                                  data-testid={`edit-tier-${u._id}`}
                                >
                                  <Settings className="w-3 h-3" />
                                  Change Tier
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                        {users.length === 0 && (
                          <tr>
                            <td colSpan={5} className="text-center py-8 text-sm" style={{ color: '#5A6B63' }}>No users found</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {/* Pagination */}
                  {usersMeta.pages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: '#E6EDE8' }}>
                      <p className="text-xs" style={{ color: '#5A6B63' }}>
                        Page {usersMeta.page} of {usersMeta.pages} ({usersMeta.total} users)
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => fetchUsers(usersMeta.page - 1)}
                          disabled={usersMeta.page <= 1}
                          className="text-xs px-3 py-1 rounded-lg disabled:opacity-40"
                          style={{ backgroundColor: '#E6EDE8', color: '#232F2A' }}
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => fetchUsers(usersMeta.page + 1)}
                          disabled={usersMeta.page >= usersMeta.pages}
                          className="text-xs px-3 py-1 rounded-lg disabled:opacity-40"
                          style={{ backgroundColor: '#E6EDE8', color: '#232F2A' }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ASSESSMENTS TAB */}
            {activeTab === "assessments" && (
              <div className="space-y-6 animate-fadeIn" data-testid="admin-assessments">
                <div className="card overflow-hidden p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr style={{ backgroundColor: '#E6EDE8' }}>
                          <th className="text-left text-xs font-medium uppercase tracking-wider px-4 py-3" style={{ color: '#5A6B63' }}>ID</th>
                          <th className="text-left text-xs font-medium uppercase tracking-wider px-4 py-3" style={{ color: '#5A6B63' }}>Province</th>
                          <th className="text-left text-xs font-medium uppercase tracking-wider px-4 py-3" style={{ color: '#5A6B63' }}>Care Path</th>
                          <th className="text-left text-xs font-medium uppercase tracking-wider px-4 py-3" style={{ color: '#5A6B63' }}>Subsidy</th>
                          <th className="text-left text-xs font-medium uppercase tracking-wider px-4 py-3" style={{ color: '#5A6B63' }}>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assessments.map(a => (
                          <tr key={a.id} className="border-t" style={{ borderColor: '#E6EDE8' }} data-testid={`assessment-row-${a.id}`}>
                            <td className="px-4 py-3">
                              <span className="text-xs font-mono" style={{ color: '#5A6B63' }}>{a.id?.slice(0, 8)}...</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm" style={{ color: '#232F2A' }}>{a.province || a.province_code || "—"}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#E6EDE8', color: '#1E3F30' }}>
                                {formatCareType(a.care_path?.primary) || "—"}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {a.subsidy_eligible ? (
                                <Check className="w-4 h-4" style={{ color: '#34694D' }} />
                              ) : (
                                <X className="w-4 h-4" style={{ color: '#B91C1C' }} />
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-xs" style={{ color: '#5A6B63' }}>
                                {a.created_at ? new Date(a.created_at).toLocaleDateString('en-CA') : "—"}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {assessments.length === 0 && (
                          <tr>
                            <td colSpan={5} className="text-center py-8 text-sm" style={{ color: '#5A6B63' }}>No assessments found</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {assessmentsMeta.pages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: '#E6EDE8' }}>
                      <p className="text-xs" style={{ color: '#5A6B63' }}>
                        Page {assessmentsMeta.page} of {assessmentsMeta.pages} ({assessmentsMeta.total} total)
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => fetchAssessments(assessmentsMeta.page - 1)}
                          disabled={assessmentsMeta.page <= 1}
                          className="text-xs px-3 py-1 rounded-lg disabled:opacity-40"
                          style={{ backgroundColor: '#E6EDE8', color: '#232F2A' }}
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => fetchAssessments(assessmentsMeta.page + 1)}
                          disabled={assessmentsMeta.page >= assessmentsMeta.pages}
                          className="text-xs px-3 py-1 rounded-lg disabled:opacity-40"
                          style={{ backgroundColor: '#E6EDE8', color: '#232F2A' }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ARTICLES TAB */}
            {activeTab === "articles" && (
              <div className="space-y-6 animate-fadeIn" data-testid="admin-articles">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-lg" style={{ color: '#232F2A' }}>
                    {adminArticles.length} Published Articles
                  </h2>
                  <button
                    onClick={() => {
                      setArticleForm({ title: "", category: "Caregiving Tips", excerpt: "", body: "", cover_image: "" });
                      setEditingArticle(null);
                      setShowArticleForm(true);
                    }}
                    className="btn-primary text-sm flex items-center gap-2"
                    data-testid="new-article-btn"
                  >
                    <FileText className="w-4 h-4" />
                    New Article
                  </button>
                </div>

                {/* Article Form */}
                {showArticleForm && (
                  <div className="card" data-testid="article-form">
                    <h3 className="font-heading text-lg mb-4" style={{ color: '#232F2A' }}>
                      {editingArticle ? "Edit Article" : "New Article"}
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium mb-1 block" style={{ color: '#232F2A' }}>Title</label>
                        <input
                          type="text"
                          value={articleForm.title}
                          onChange={e => setArticleForm({...articleForm, title: e.target.value})}
                          className="input-base w-full"
                          placeholder="Article title..."
                          data-testid="article-title-input"
                        />
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium mb-1 block" style={{ color: '#232F2A' }}>Category</label>
                          <select
                            value={articleForm.category}
                            onChange={e => setArticleForm({...articleForm, category: e.target.value})}
                            className="input-base w-full"
                            data-testid="article-category-select"
                          >
                            <option>Caregiving Tips</option>
                            <option>Care Options</option>
                            <option>Financial Planning</option>
                            <option>Caregiver Wellness</option>
                            <option>Health & Safety</option>
                            <option>Legal & Planning</option>
                            <option>Community Resources</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-1 block" style={{ color: '#232F2A' }}>Cover Image URL</label>
                          <input
                            type="text"
                            value={articleForm.cover_image}
                            onChange={e => setArticleForm({...articleForm, cover_image: e.target.value})}
                            className="input-base w-full"
                            placeholder="https://images.unsplash.com/..."
                            data-testid="article-image-input"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block" style={{ color: '#232F2A' }}>Excerpt (short summary)</label>
                        <input
                          type="text"
                          value={articleForm.excerpt}
                          onChange={e => setArticleForm({...articleForm, excerpt: e.target.value})}
                          className="input-base w-full"
                          placeholder="Brief description for article cards..."
                          data-testid="article-excerpt-input"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block" style={{ color: '#232F2A' }}>Body (use **bold** for headings)</label>
                        <textarea
                          value={articleForm.body}
                          onChange={e => setArticleForm({...articleForm, body: e.target.value})}
                          className="input-base w-full"
                          rows={12}
                          placeholder="Write your article here..."
                          data-testid="article-body-input"
                        />
                      </div>
                      <div className="flex gap-3">
                        <button onClick={saveArticle} className="btn-primary text-sm" data-testid="save-article-btn">
                          {editingArticle ? "Update Article" : "Publish Article"}
                        </button>
                        <button
                          onClick={() => { setShowArticleForm(false); setEditingArticle(null); }}
                          className="btn-secondary text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Articles List */}
                <div className="space-y-3">
                  {adminArticles.map(article => (
                    <div key={article.id} className="card flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid={`admin-article-${article.id}`}>
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        {article.cover_image && (
                          <img src={article.cover_image} alt="" className="w-16 h-12 rounded-lg object-cover flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <h4 className="font-medium text-sm truncate" style={{ color: '#232F2A' }}>{article.title}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#E6EDE8', color: '#1E3F30' }}>{article.category}</span>
                            <span className="text-xs" style={{ color: '#5A6B63' }}>
                              {article.created_at ? new Date(article.created_at).toLocaleDateString('en-CA') : ""}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button onClick={() => startEditArticle(article)} className="text-xs px-3 py-1.5 rounded-full" style={{ backgroundColor: '#E6EDE8', color: '#1E3F30' }}>
                          Edit
                        </button>
                        <button onClick={() => deleteArticle(article.id)} className="text-xs px-3 py-1.5 rounded-full hover:bg-red-50" style={{ color: '#B91C1C' }}>
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* PROVIDERS TAB */}
            {activeTab === "providers" && stats && (
              <div className="space-y-6 animate-fadeIn" data-testid="admin-providers">
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="card text-center">
                    <p className="font-heading text-3xl mb-1" style={{ color: '#1E3F30' }}>{stats.total_providers}</p>
                    <p className="text-sm" style={{ color: '#5A6B63' }}>Total Providers</p>
                  </div>
                  <div className="card text-center">
                    <p className="font-heading text-3xl mb-1" style={{ color: '#1E3F30' }}>{stats.pending_claims}</p>
                    <p className="text-sm" style={{ color: '#5A6B63' }}>Pending Claims</p>
                  </div>
                  <div className="card text-center">
                    <Link to="/providers" className="font-heading text-lg hover:underline" style={{ color: '#1E3F30' }}>
                      View Full Directory
                    </Link>
                    <p className="text-sm mt-1" style={{ color: '#5A6B63' }}>Browse all listed providers</p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/assessment" element={<Assessment />} />
            <Route path="/report" element={<ReportPage />} />
            <Route path="/providers" element={<ProviderDirectory />} />
            <Route path="/compare" element={<ComparisonTool />} />
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/coordinators" element={<CoordinatorMarketplace />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/articles" element={<ArticlesPage />} />
            <Route path="/articles/:slug" element={<ArticlePageWrapper />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/signed-out" element={<SignedOutPage />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
